<template>
  <div class="movie-content" :class="{ 'is-region-loading': regionRefreshing }">
    <!-- 本日全国サマリー：特定の作品に紐づかない日次集計のため、詳細ドロワーに依存せず常設表示する -->
    <div
      class="today-dashboard today-dashboard-block bg"
      :class="{ 'mp-summary': isMobileLayout }"
    >
      <!-- PC：作品リスト上部の通栏バナー -->
      <template v-if="!isMobileLayout">
        <template v-if="showSkeleton">
          <div class="skeleton-block skeleton-title" />
          <div class="skeleton-block skeleton-big" />
          <div class="today-dashboard-view">
            <div class="skeleton-block skeleton-inline" />
            <div class="skeleton-block skeleton-inline" />
          </div>
        </template>
        <template v-else>
          <div class="today-dashboard-title">{{ nationBoxInfo.title }}</div>
          <div class="nation-box">
            <span class="nation-box-num">{{ nationBoxInfo.boxOffice }}</span>
            <span class="unit">{{ nationBoxInfo.unit }}</span>
          </div>
          <div class="today-dashboard-divider" />
          <div class="today-dashboard-view">
            <p>総動員：{{ nationBoxInfo.tickets }}万人</p>
            <p>総上映：{{ nationBoxInfo.showCount }}回</p>
          </div>
        </template>
      </template>

      <!-- スマホ：mp-summary -->
      <template v-else>
        <template v-if="showSkeleton">
          <div class="mp-summary__topbar">
            <div class="skeleton-block skeleton-inline" />
            <div class="skeleton-block skeleton-inline" />
          </div>
          <div class="skeleton-block skeleton-title" />
          <div class="skeleton-block skeleton-big" />
          <div class="mp-summary__stats">
            <div class="skeleton-block skeleton-inline" />
            <div class="skeleton-block skeleton-inline" />
          </div>
        </template>
        <template v-else>
          <div class="mp-summary__topbar">
            <span class="mp-summary__mid">
              <span class="mp-summary__type">日別興収</span>
              <span class="mp-summary__date">{{ boxDateLabel }}</span>
            </span>
          </div>

          <div class="mp-summary__season">
            {{ nationBoxInfo.title }}
          </div>

          <div class="mp-summary__center">
            <button
              v-if="!isRange"
              type="button"
              class="mp-daynav mp-daynav--prev"
              @click="goPrevDay"
            >
              前<br>日
            </button>
            <div class="mp-summary__figure">
              <div class="mp-summary__amount">
                <span class="mp-summary__num">
                  <SlotNumber :value="nationBoxInfo.boxOffice" />
                </span>
                <span class="mp-summary__unit">{{ nationBoxInfo.unit }}</span>
              </div>
            </div>
            <button
              v-if="!isRange"
              type="button"
              class="mp-daynav mp-daynav--next"
              @click="goNextDay"
            >
              翌<br>日
            </button>
          </div>

          <div class="mp-summary__stats">
            <div class="mp-summary__stat">
              <span class="mp-summary__stat-value">
                {{ nationBoxInfo.tickets }}<span class="mp-summary__stat-unit">万人</span>
              </span>
              <span class="mp-summary__stat-label">総動員</span>
            </div>
            <div class="mp-summary__stat">
              <span class="mp-summary__stat-value">
                {{ nationBoxInfo.showCount }}<span class="mp-summary__stat-unit">回</span>
              </span>
              <span class="mp-summary__stat-label">総上映回数</span>
            </div>
          </div>
        </template>
      </template>
    </div>

    <div class="dashboard-list dashboard-left bg">
      <div class="dashboard-table-header">
        <div class="left-box">
          <div class="table-header">映画興行</div>
        </div>
      </div>

      <div
        class="movielist-container"
        :class="{ 'is-loading': showSkeleton || listRefreshing }"
      >
        <div v-if="showSkeleton" class="movie-skeleton-wrap">
          <div v-for="idx in 7" :key="`left-sk-${idx}`" class="movie-skeleton-row">
            <div class="skeleton-block skeleton-rank" />
            <div class="skeleton-movie-meta">
              <div class="skeleton-block skeleton-main" />
              <div class="skeleton-block skeleton-sub" />
            </div>
            <div class="skeleton-block skeleton-cell" />
            <div class="skeleton-block skeleton-cell" />
            <div class="skeleton-block skeleton-cell" />
            <div class="skeleton-block skeleton-cell" />
            <div class="skeleton-block skeleton-cell last" />
          </div>
        </div>
        <div
          v-show="!showSkeleton"
          class="movielist"
          @scroll="onMovielistScroll"
        >
          <table class="dashboard-table">
            <thead>
              <tr>
                <th class="moviename-th">
                  <div>
                    <span>作品</span>
                  </div>
                </th>
                <th class="box-title-tab">
                  <div class="theader">{{ boxColumnTitle }}</div>
                </th>
                <th><div class="theader">興行シェア</div></th>
                <th><div class="theader">上映回数</div></th>
                <th><div class="theader">上映シェア</div></th>
                <th><div class="theader">上映あたり平均動員</div></th>
                <th><div class="theader last-col">充足率</div></th>
              </tr>
            </thead>
            <tbody v-if="!listRefreshing">
              <tr v-if="!loading && !movies.length">
                <td class="movie-empty" colspan="7">データがありません</td>
              </tr>
              <tr
                v-for="(movie, index) in movies"
                :key="movie.id"
                :class="{ 'selected-movie': index === highlightIndex }"
                @click="selectMovie(index)"
              >
                <td class="moviename-td" :title="movie.name">
                  <div class="movie-desc">
                    <div class="moviename-num">
                      <p class="moviename-index">{{ formatRank(movie.originalRank) }}</p>
                    </div>
                    <div class="moviename-poster">
                      <span class="moviename-poster-sheen" aria-hidden="true" />
                      <img
                        class="moviename-poster-img"
                        :src="movie.imgUrl || defaultPosterImg"
                        :alt="movie.name"
                        loading="lazy"
                        @error="onListPosterError"
                      >
                    </div>
                    <div class="moviename-desc">
                      <p class="moviename-name">{{ movie.name }}</p>
                      <p class="moviename-info">
                        <span>{{ movie.releaseInfo }}</span>
                        <span>{{ movie.cumulativeMaxBoxOfficeText }}</span>
                      </p>
                    </div>
                  </div>
                </td>
                <td class="realtime" :data-label="boxColumnTitle">{{ movie.boxOffice }}{{ movie.unit }}</td>
                <td data-label="興行シェア">{{ movie.boxRate }}</td>
                <td data-label="上映回数">{{ formatNumber(movie.showCount) }}</td>
                <td data-label="上映シェア">{{ movie.showCountRate }}</td>
                <td data-label="平均動員">{{ movie.avgShowView }}</td>
                <td class="last-tab" data-label="充足率">{{ movie.avgSeatView }}</td>
              </tr>
            </tbody>
          </table>
          <!-- maoyan-pro mp-rank__state 同型：行を置き換えて中央に読み込み表示 -->
          <div v-if="listRefreshing" class="movie-list-state" :class="{ 'is-region': regionRefreshing }" aria-live="polite">
            <template v-if="regionRefreshing">
              <div class="region-loading-inline" aria-hidden="true">
                <span class="region-loading-inline__ripple" />
                <span class="region-loading-inline__ripple region-loading-inline__ripple--2" />
                <span class="region-loading-inline__ripple region-loading-inline__ripple--3" />
                <span class="region-loading-inline__dot" />
              </div>
              <p class="region-loading-inline__text">{{ regionLabel }} のデータを更新中</p>
            </template>
            <template v-else>
              <span class="movie-list-state__loading">
                <i class="fas fa-spinner fa-spin" aria-hidden="true" /> 読み込み中…
              </span>
            </template>
          </div>
          <!-- DEV-14789：有料版の下端スクロール追加読み込み。既存行は残したまま末尾に軽量表示 -->
          <div v-if="!listRefreshing && loadingMore" class="movie-list-load-more" aria-live="polite">
            <i class="fas fa-spinner fa-spin" aria-hidden="true" /> 読み込み中…
          </div>
        </div>
      </div>
    </div>

    <Transition name="region-loading-fade">
      <div
        v-if="regionRefreshing"
        class="region-loading-overlay"
        aria-live="polite"
        aria-busy="true"
      >
        <div class="region-loading-card">
          <div class="region-loading-radar" aria-hidden="true">
            <span class="region-loading-radar__grid" />
            <span class="region-loading-radar__ripple" />
            <span class="region-loading-radar__ripple region-loading-radar__ripple--2" />
            <span class="region-loading-radar__ripple region-loading-radar__ripple--3" />
            <span class="region-loading-radar__sweep" />
            <span class="region-loading-radar__core">
              <i class="fas fa-map-marker-alt" />
            </span>
          </div>
          <div class="region-loading-card__badge">{{ regionLabel }}</div>
          <div class="region-loading-card__title">データを読み込み中</div>
          <div class="region-loading-card__hint">興行ランキングを更新しています</div>
        </div>
      </div>
    </Transition>

    <Transition name="idle-fade">
      <div v-if="isIdle" class="idle-overlay">
        <div class="idle-overlay__card">
          <div class="idle-overlay__icon">
            <span class="idle-overlay__moon"></span>
            <span class="idle-overlay__zzz idle-overlay__zzz--1">z</span>
            <span class="idle-overlay__zzz idle-overlay__zzz--2">z</span>
            <span class="idle-overlay__zzz idle-overlay__zzz--3">z</span>
          </div>
          <div class="idle-overlay__title">離席中</div>
          <div class="idle-overlay__text">しばらく操作がなかったため、自動更新を一時停止しました</div>
          <div class="idle-overlay__hint">画面をクリックすると再開します</div>
        </div>
      </div>
    </Transition>
  </div>

  <!-- 作品詳細ページ：行クリックで独立ページとして開き、左上の「戻る」で一覧に戻る -->
  <MovieDetailPage :open="detailOpen" @close="onDetailBack">
    <div v-if="showSkeleton" class="dashboard-detail dashboard-detail--skeleton">
      <div class="movie-detail bg">
        <div class="movie-detail-hero">
          <div class="poster-img">
            <div class="skeleton-poster-card">
              <div class="skeleton-poster-glow" />
              <div class="skeleton-poster-footer">
                <div class="skeleton-block skeleton-poster-line skeleton-poster-line-main" />
                <div class="skeleton-block skeleton-poster-line" />
              </div>
            </div>
          </div>
          <div class="movie-detail-title">
            <div class="skeleton-title-lines">
              <div class="skeleton-block skeleton-title-main-line" />
              <div class="skeleton-block skeleton-title-sub-line" />
              <div class="skeleton-block skeleton-title-sub-line skeleton-title-sub-line-short" />
            </div>
          </div>
        </div>
        <div class="movie-box">
          <div class="movie-box-detail movie-box-total">
            <div class="movie-box-block"><div class="skeleton-block skeleton-cell" /></div>
            <div class="movie-box-block"><div class="skeleton-block skeleton-cell" /></div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="selectedMovie" class="dashboard-detail">
      <div class="movie-detail bg">
        <div class="movie-detail-hero">
          <div class="poster-img">
            <div v-if="!posterLoaded && !posterUseFallback && selectedMovie.imgUrl" class="poster-loading">
              <div class="poster-loading__sheen" />
              <i class="fas fa-film poster-loading__icon" />
            </div>
            <img
              ref="posterImgRef"
              :key="`${selectedMovie.movieId}-${selectedMovie.imgUrl || 'fallback'}`"
              :src="selectedMovie.imgUrl && !posterUseFallback ? selectedMovie.imgUrl : defaultPosterImg"
              :alt="selectedMovie.name"
              class="poster-img__photo"
              :class="{ 'is-loaded': posterLoaded }"
              @load="onPosterLoad"
              @error="onPosterError"
            >
            <div v-if="posterUseFallback" class="poster-fallback">
              <span class="poster-fallback__name">{{ selectedMovie.name }}</span>
            </div>
          </div>
          <div class="movie-detail-title">
            <p v-if="!posterUseFallback" class="movie-title">
              {{ selectedMovie.name }}
              <i class="fas fa-star movie-title-star" />
            </p>
            <p v-if="selectedMovie.genres" class="movie-box-data">{{ selectedMovie.genres }}</p>
            <p v-if="selectedMovie.category" class="movie-box-data">{{ selectedMovie.category }}</p>
            <p class="movie-box-data">
              {{ selectedMovie.releaseInfo }}
              {{ selectedMovie.sumBoxDesc }}
            </p>
          </div>
        </div>

        <div class="movie-data-bar">
          <div class="movie-box">
            <div class="movie-box-detail movie-box-total">
              <div class="movie-box-block">
                <p class="box-title">{{ movieBoxTitle }}</p>
                <p class="sum-box-desc">
                  <span>{{ selectedMovie.boxOffice }}</span>
                  <span class="sum-box-unit">{{ selectedMovie.unit }}</span>
                </p>
              </div>
              <div class="movie-box-block">
                <p class="box-title">興行シェア</p>
                <p class="sum-box-num">{{ selectedMovie.boxRate }}</p>
              </div>
            </div>

            <div class="movie-box-detail movie-box-view">
              <div class="movie-box-block">
                <p class="box-title">{{ showCountTitle }}</p>
                <p class="sum-box-num">{{ formatNumber(selectedMovie.showCount) }}</p>
                <p class="box-extr">上映シェア {{ selectedMovie.showCountRate }}</p>
              </div>
              <div class="movie-box-block">
                <p class="box-title">{{ seatRateTitle }}</p>
                <p class="sum-box-num">{{ selectedMovie.avgSeatView }}</p>
                <p class="box-extr">上映あたり平均動員 {{ selectedMovie.avgShowView }}</p>
              </div>
            </div>
          </div>

          <!-- 最終興行収入予測（DEV-14974）：直近興行トレンドの上に表示；公開版は非表示 -->
          <div v-if="selectedMovie && !publicMode" class="mbox-chart mbox-prediction">
            <div class="chart-title">最終興行収入予測</div>
            <div class="prediction-body">
              <FinalPredictionBody
                :prediction="boxOfficePrediction"
                :loading="boxOfficePredictionLoading"
              />
            </div>
          </div>

          <div
            v-if="selectedMovie && (selectedMovie.boxTrends?.length || trendLoading)"
            class="mbox-chart"
          >
            <div class="chart-title">直近興行トレンド</div>
            <!-- 公開週セレクタ（有料版のみ・DEV-14945）：劇場週次詳細と同じ金曜〜木曜の週で切り替える -->
            <div v-if="currentTrendWeek" class="trend-weeks">
              <button
                type="button"
                class="trend-weeks__nav"
                :disabled="!canPrevTrendWeek"
                aria-label="前の週"
                @click="stepTrendWeek(-1)"
              >
                <i class="fas fa-chevron-left" aria-hidden="true" />
              </button>
              <div ref="trendWeeksScrollRef" class="trend-weeks__scroll">
                <button
                  v-for="n in trendWeekNoList"
                  :key="`trend-week-${n}`"
                  type="button"
                  class="trend-weeks__item"
                  :class="{ 'is-active': n === currentTrendWeek.weekNo }"
                  :aria-pressed="n === currentTrendWeek.weekNo"
                  @click="selectTrendWeek(n)"
                >
                  {{ formatWeekLabel(n) }}
                </button>
              </div>
              <button
                type="button"
                class="trend-weeks__nav"
                :disabled="!canNextTrendWeek"
                aria-label="次の週"
                @click="stepTrendWeek(1)"
              >
                <i class="fas fa-chevron-right" aria-hidden="true" />
              </button>
            </div>
            <p v-if="currentTrendWeek" class="trend-weeks__range">{{ trendWeekRangeText }}</p>
            <div class="chart-body">
              <div v-if="trendLoading && !selectedMovie.boxTrends?.length" class="chart-loading">
                <span class="chart-loading-spinner" aria-label="読み込み中" />
              </div>
              <div
                v-else-if="selectedMovie.boxTrends?.length"
                :key="trendChartKey"
                class="chart-box"
                :class="{ 'is-loading': trendLoading }"
              >
                <div
                  v-for="(item, index) in selectedMovie.boxTrends"
                  :key="item.date || item.label"
                  class="chart-box-item"
                  :class="{
                    'has-tip': item.tipInfo,
                    'tip-on-right': index < selectedMovie.boxTrends.length / 2,
                    'is-tapped': item.tipInfo && item.date === tappedTrendTipDate,
                  }"
                  @click="onTrendItemTap(item)"
                >
                  <div
                    class="bar-boxnum bar-fade-in"
                    :class="{ 'is-forecast': item.hasForecast, 'is-no-forecast': item.forecastStatus === 'UNAVAILABLE' }"
                    :style="{ animationDelay: `${index * 0.07}s` }"
                  >
                    <!-- 日別予測（DEV-14975）：予測のある日は「予測」タグ + 予測値、予測なしの日は注記 -->
                    <span v-if="item.hasForecast" class="bar-boxnum__tag">予測</span>
                    {{ item.value }}
                    <span v-if="item.forecastStatus === 'UNAVAILABLE'" class="bar-boxnum__none">予測なし</span>
                  </div>
                  <div class="bar-chart-wrap">
                    <!-- 予測あり：上段 = 予測の残り（斜線）、下段 = 販売済み。棒の高さ = 予測値 -->
                    <div
                      v-if="item.hasForecast"
                      class="bar-stack bar-grow-in"
                      :style="{
                        height: `${item.height}px`,
                        animationDelay: `${index * 0.07}s`,
                      }"
                    >
                      <div class="bar-forecast" :style="{ height: `${item.forecastHeight}px` }" />
                      <div v-if="item.soldHeight > 0" class="bar-chart bar-chart--sold" :style="{ height: `${item.soldHeight}px` }" />
                    </div>
                    <div
                      v-else
                      class="bar-chart bar-grow-in"
                      :style="{
                        height: `${item.height}px`,
                        animationDelay: `${index * 0.07}s`,
                      }"
                    />
                  </div>
                  <div
                    class="bar-data bar-fade-in"
                    :class="{ 'is-weekend': item.isWeekend, 'is-today': item.isToday }"
                    :style="{ animationDelay: `${index * 0.07 + 0.12}s` }"
                  >
                    {{ item.label }}
                    <span v-if="item.dayOfWeek" class="bar-data__dow">{{ item.dayOfWeek }}</span>
                  </div>
                  <!-- 予測日のホバー詳細：列のどこに乗っても即表示（タッチ端末はタップで開閉）。左半分の列は右側、右半分の列は左側に出す -->
                  <div v-if="item.tipInfo" class="trend-tip" role="tooltip">
                    <p class="trend-tip__title">{{ item.tipInfo.title }}</p>
                    <p class="trend-tip__row">
                      <i class="trend-tip__swatch lg-sold" />
                      <span class="trend-tip__label">販売済み</span>
                      <span class="trend-tip__value">{{ item.tipInfo.sold }}</span>
                    </p>
                    <p class="trend-tip__row">
                      <i class="trend-tip__swatch lg-forecast" />
                      <span class="trend-tip__label">予測（残り）</span>
                      <span class="trend-tip__value">{{ item.tipInfo.remain }}</span>
                    </p>
                    <p class="trend-tip__row is-total">
                      <i class="trend-tip__swatch" />
                      <span class="trend-tip__label">予測合計</span>
                      <span class="trend-tip__value">{{ item.tipInfo.total }}</span>
                    </p>
                    <p v-if="item.tipInfo.time" class="trend-tip__sub">{{ item.tipInfo.time }}</p>
                  </div>
                </div>
              </div>
              <!-- 日別予測の凡例（DEV-14975）：今日〜明後日を含む週だけ表示 -->
              <div v-if="trendForecast" class="trend-forecast-legend">
                <template v-if="trendForecast.regionFiltered">
                  <span>地域絞り込み中は予測を表示しません（予測は全国のみ）</span>
                </template>
                <template v-else-if="trendForecast.hasForecast">
                  <span class="trend-forecast-legend__item"><i class="lg-sold" />販売済み</span>
                  <span class="trend-forecast-legend__item"><i class="lg-forecast" />予測（残り）</span>
                  <span v-if="trendForecast.latestTime" class="trend-forecast-legend__time">{{ trendForecast.latestTime }} 時点</span>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CinemaContent
        v-if="showCinemaSection"
        ref="embeddedCinemaContentRef"
        embedded
        :movie="selectedMovie"
        :selected-date="selectedDate"
        :start-date="rangeStart"
        :initial-area-ids="props.areaIds"
        :initial-prefecture-ids="props.prefectureIds"
        :initial-nearby="props.nearby"
        :opened-cinema-id="props.openedCinemaId"
        @open-cinema="(cinema) => emit('open-cinema', { cinema, movieId: selectedMovie?.movieId ?? null })"
      />
    </div>
  </MovieDetailPage>
</template>

<script setup>
import { computed, nextTick, onActivated, onDeactivated, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import dayjs from 'dayjs'
import defaultPosterImg from '@/assets/images/default-poster.png'
import {
  getMovieDailyTrendService,
  getMovieMarketRankService,
  getMovieWeeklyTrendService,
  getBoxOfficePredictionService,
  getPublicMovieDailyTrendService,
  getPublicMovieMarketRankService,
} from '@/api/dp/index.js'
import { buildDateParams, formatDateLabel, formatRangeLabel, isRangeMode } from '@/utils/dashboard-date-range'
import { buildWeekNoList, formatWeekLabel, formatWeekRange } from '@/utils/dashboard-cinema-rank.js'
import {
  createEmptyNationBoxInfo,
  mapDailyTrends,
  mapMovieRankItem,
  mapNationBoxInfo,
  mapWeeklyTrends,
  pickDailyTrendList,
  pickMarketRankList,
  pickMarketRankSummary,
  summarizeWeeklyForecast,
} from '@/utils/dashboard-market-rank.js'
import CinemaContent from './cinema-content.vue'
import MovieDetailPage from './movie-detail-page.vue'
import SlotNumber from './slot-number.vue'
// 最終興行収入予測カード本体（DEV-14891 発行方ページと共用，不另算模型）
import FinalPredictionBody from '@/views/dp/client-v2/components/final-prediction-body.vue'

const props = defineProps({
  /** 業務日レンジの終点。単日モードでは選択日そのもの（累計/上映N日などの基準日でもある） */
  selectedDate: {
    type: Object,
    default: () => dayjs(),
  },
  /** 業務日レンジの起点。未指定なら selectedDate と同日＝単日モード */
  startDate: {
    type: Object,
    default: null,
  },
  paused: {
    type: Boolean,
    default: false,
  },
  forceRefreshKey: {
    type: Number,
    default: 0,
  },
  softRefreshKey: {
    type: Number,
    default: 0,
  },
  /** 地区選択（親で一元管理、複数選択）：空配列 = 全国 */
  areaIds: {
    type: Array,
    default: () => [],
  },
  prefectureIds: {
    type: Array,
    default: () => [],
  },
  /** 「近隣50km」モード。areaIds/prefectureIds とは排他で、true のとき両者は必ず空 */
  nearby: {
    type: Boolean,
    default: false,
  },
  regionLabel: {
    type: String,
    default: '全国',
  },
  // 公開無料版ダッシュボード用：/open/* の匿名エンドポイントに切り替える（当日+全国固定）
  publicMode: {
    type: Boolean,
    default: false,
  },
  /** 週次詳細を開いている劇場ID（embedded CinemaContent の行ハイライト用。状態は index.vue 側で保持） */
  openedCinemaId: {
    type: Number,
    default: null,
  },
})

const emit = defineEmits(['change-date', 'open-cinema', 'close-detail'])

// 公開無料版ダッシュボードは地域筛选も劇場別ドリルダウンも提供しない（/open/* は地域パラメータ非対応）
const showCinemaSection = computed(() => !props.publicMode)
const embeddedCinemaContentRef = ref(null)
defineExpose({
  currentVersionParams: () => embeddedCinemaContentRef.value?.currentVersionParams?.() ?? {},
})

const marketRankService = computed(() => (props.publicMode ? getPublicMovieMarketRankService : getMovieMarketRankService))
const dailyTrendService = computed(() => (props.publicMode ? getPublicMovieDailyTrendService : getMovieDailyTrendService))
/**
 * 有料版の「直近興行トレンド」は公開週（金曜〜木曜）単位で表示する（DEV-14945）。
 * 無料版は /open/* に週次の口がないため、従来どおり直近5日のまま。
 */
const useWeeklyTrend = computed(() => !props.publicMode)

/** レンジ起点（親が渡さなければ終点と同日＝単日モード） */
const rangeStart = computed(() => dayjs(props.startDate ?? props.selectedDate))
/** 期間モードか。単日モードのときだけ「前日/翌日」やカレンダー連動の文言を出す */
const isRange = computed(() => isRangeMode(rangeStart.value, props.selectedDate))

const boxDateLabel = computed(() => (isRange.value
  ? formatRangeLabel(rangeStart.value, props.selectedDate)
  : formatDateLabel(props.selectedDate)))

function goPrevDay() {
  emit('change-date', dayjs(props.selectedDate).subtract(1, 'day'))
}

// 未来日（先行販売）も見られるようにするため、翌日方向の上限は設けない
function goNextDay() {
  emit('change-date', dayjs(props.selectedDate).startOf('day').add(1, 'day'))
}

const MOBILE_BREAKPOINT_MQ = '(max-width: 768px)'
/** 作品詳細ページの開閉を載せる query キー */
const DETAIL_QUERY_KEY = 'movie'
const route = useRoute()
const router = useRouter()
/** 詳細を push で開いたか。「戻る」で履歴を1つ戻せるかの判定に使う */
let detailOpenedByPush = false
/**
 * 作品詳細ページの開閉。
 * 実体は同一ルート上のビュー切り替えだが、開閉を query(?movie=) に載せることで
 * ブラウザの戻る／スマホのバックジェスチャーで一覧に戻れるようにしている。
 */
const detailOpen = ref(false)
const isMobileLayout = ref(
  typeof window !== 'undefined' && window.matchMedia(MOBILE_BREAKPOINT_MQ).matches,
)
let mobileMq = null

function syncMobileLayout() {
  const next = mobileMq ? mobileMq.matches : isMobileViewport()
  isMobileLayout.value = next
}

function isMobileViewport() {
  return typeof window !== 'undefined' && window.matchMedia(MOBILE_BREAKPOINT_MQ).matches
}

/**
 * プログラム都合で詳細を閉じる（日付・地域の変更など）。
 * 履歴を1つ戻すのではなく現在のエントリから query を外す。
 */
function closeDetail() {
  if (route.query[DETAIL_QUERY_KEY] == null) return
  const query = { ...route.query }
  delete query[DETAIL_QUERY_KEY]
  void router.replace({ query })
}

/**
 * 「戻る」ボタン。push で開いていれば履歴を1つ戻す
 * （replace で消すとブラウザの戻るが一度空振りして見えるため）。
 * ?movie= 付きの URL で直接開かれた場合は戻り先が無いので query を外すだけにする。
 */
function onDetailBack() {
  if (detailOpenedByPush) {
    detailOpenedByPush = false
    router.back()
    return
  }
  closeDetail()
}

/** query から詳細ページの開閉と選択作品を復元する（戻る／進む／リロードの唯一の入口） */
function syncDetailFromRoute() {
  const raw = route.query[DETAIL_QUERY_KEY]
  const movieId = raw == null || raw === '' ? null : Number(raw)
  if (movieId == null || Number.isNaN(movieId)) {
    const wasOpen = detailOpen.value
    selectedMovieId.value = null
    detailOpen.value = false
    detailOpenedByPush = false
    // 詳細ページの上に重なっている週次詳細ドロワーも一緒に閉じる
    // （ブラウザバックで詳細だけ消えてドロワーが宙に浮くのを防ぐ）
    if (wasOpen) emit('close-detail')
    return
  }
  if (selectedMovieId.value === movieId && detailOpen.value) return
  selectedMovieId.value = movieId
  detailOpen.value = true
}

watch(() => route.query[DETAIL_QUERY_KEY], syncDetailFromRoute)

watch(detailOpen, (open) => {
  if (!open) return
  // シート表示後にキャッシュ済み画像の complete を再判定（load 取り逃し対策）
  nextTick(() => {
    const img = posterImgRef.value
    if (img?.complete && img.naturalWidth > 0) {
      posterLoaded.value = true
    }
  })
})

const loading = ref(false)
const initialLoading = ref(true)
const showSkeletonForNextFetch = ref(false)
/** 前日/翌日の静默刷新中：リスト上に載入マスクを出す */
const listRefreshing = ref(false)
/** 地区切替時の強調ローディング */
const regionRefreshing = ref(false)
const movies = ref([])
const nationBoxInfo = ref(createEmptyNationBoxInfo())
// 记录选中的电影 id，列表顺序始终按排名展示，不因选中而置顶
const selectedMovieId = ref(null)
const trendLoading = ref(false)
/**
 * ユーザーが明示的に選んだ公開週。null の間はサーバー側の既定（今日（JST）の属する週）に任せる。
 * 選んだ週は 10 分ごとのポーリング更新でも維持し、作品を切り替えたら null に戻す。
 */
const trendWeekNo = ref(null)
/** 直近に取得できた公開週の情報 { movieId, weekNo, minWeekNo, maxWeekNo, weekStart, weekEnd } */
const trendWeek = ref(null)
const trendWeeksScrollRef = ref(null)

/** サーバー側デフォルトと同じ値（DEV-14789：有料版は下端スクロールでこの単位ずつ増やして再取得する） */
const DEFAULT_MOVIE_LIMIT = 25
const LOAD_MORE_STEP = 25
const marketRankLimit = ref(DEFAULT_MOVIE_LIMIT)
/** 直前の取得件数が limit と同数だった＝まだ続きがある可能性。無料版は常に false（/open は limit 固定） */
const hasMoreMovies = ref(false)
/** 下端スクロールでの追加読み込み中。骨格やリスト全体のマスクとは別の軽量な表示に使う */
const loadingMore = ref(false)

let marketRankFetchId = 0
let dailyTrendSessionId = 0
let marketRankLoopTimer = null
let marketRankLoopStopped = false
let marketRankAbortController = null
let dailyTrendAbortController = null
let dailyTrendFetchChain = Promise.resolve()
const MARKET_RANK_LOOP_INTERVAL_MS = 10 * 60 * 1000
/** 半小时无操作后停止 10 分钟轮询，用户再次操作时恢复并立即刷新 */
// TODO: 测试用临时值，验证后改回 30 * 60 * 1000
const IDLE_STOP_MS = 30 * 60 * 1000
const IDLE_ACTIVITY_EVENTS = ['pointerdown', 'mousemove', 'keydown', 'wheel', 'touchstart', 'scroll']
let idleStopTimer = null
let idleStopped = false
let lastIdleResetAt = 0
// 空闲停止后展示“離席中”覆盖层
const isIdle = ref(false)
// 当前是否停留在 /index 页面（keep-alive 激活中且浏览器标签可见）
let pageActive = false

const today = dayjs().startOf('day')
// 単日モードの「本日/当日」判定はレンジ終点で行う（期間モードでは下の文言側で期間扱いに切り替える）
const isToday = computed(() => !isRange.value && props.selectedDate.startOf('day').isSame(today))
const isFuture = computed(() => !isRange.value && props.selectedDate.startOf('day').isAfter(today))

const boxColumnTitle = computed(() => (isFuture.value ? '先行興行' : '興行収入（AI推測値）'))
const movieBoxTitle = computed(() => {
  if (isRange.value) return '期間興行収入'
  if (isFuture.value) return '当日先行興行'
  if (isToday.value) return '本日興行収入'
  return '当日興行収入'
})
const showCountTitle = computed(() => {
  if (isRange.value) return '期間上映回数'
  return isToday.value ? '本日上映回数' : '当日上映回数'
})
const seatRateTitle = computed(() => {
  if (isRange.value) return '期間充足率'
  return isToday.value ? '本日充足率' : '当日充足率'
})
const showSkeleton = computed(() => (initialLoading.value || showSkeletonForNextFetch.value) && loading.value)

// 选中项在当前排名列表中的下标：未选中时不高亮任何行（PC/スマホ共通）
const selectedIndex = computed(() => {
  if (selectedMovieId.value == null) return -1
  const idx = movies.value.findIndex((item) => item.movieId === selectedMovieId.value)
  return idx
})
const highlightIndex = computed(() => selectedIndex.value)

const selectedMovie = computed(() => {
  if (selectedMovieId.value == null) return null
  return movies.value.find((item) => item.movieId === selectedMovieId.value) ?? null
})

/**
 * 対象作品が確定したら日次トレンドを取りに行く。
 * ?movie= 付きでリロードされた場合、一覧が届くまで selectedMovie は null なので、
 * 「作品が確定した瞬間」を単一のトリガーにする（selectMovie と復元で二重に叩かない）。
 */
watch(
  () => selectedMovie.value?.movieId,
  (movieId, prevMovieId) => {
    if (movieId == null || movieId === prevMovieId) return
    // 作品が変わったら週の選択はリセットし、その作品の「今日の属する週」から見せる
    trendWeekNo.value = null
    trendWeek.value = null
    void fetchDailyTrendForSelectedMovie({ resetPolling: true })
    void fetchBoxOfficePrediction(movieId)
  },
)

/** 表示中の作品に対応する公開週情報。別作品の週情報が一瞬残って見えるのを防ぐため movieId で照合する */
const currentTrendWeek = computed(() => {
  if (!useWeeklyTrend.value) return null
  const week = trendWeek.value
  if (!week || week.movieId !== selectedMovie.value?.movieId) return null
  return week
})
const trendWeekNoList = computed(() => (currentTrendWeek.value
  ? buildWeekNoList(currentTrendWeek.value.minWeekNo, currentTrendWeek.value.maxWeekNo)
  : []))
const trendWeekRangeText = computed(() => (currentTrendWeek.value
  ? formatWeekRange(currentTrendWeek.value.weekStart, currentTrendWeek.value.weekEnd)
  : ''))
const canPrevTrendWeek = computed(() => !!currentTrendWeek.value
  && currentTrendWeek.value.weekNo > currentTrendWeek.value.minWeekNo)
const canNextTrendWeek = computed(() => !!currentTrendWeek.value
  && currentTrendWeek.value.weekNo < currentTrendWeek.value.maxWeekNo)

function selectTrendWeek(n) {
  if (!currentTrendWeek.value || n === currentTrendWeek.value.weekNo) return
  trendWeekNo.value = n
  void fetchDailyTrendForSelectedMovie()
}

function stepTrendWeek(delta) {
  const week = currentTrendWeek.value
  if (!week) return
  const next = week.weekNo + delta
  if (next < week.minWeekNo || next > week.maxWeekNo) return
  selectTrendWeek(next)
}

/** 選択中の週ボタンが横スクロールの外に隠れないよう、見える位置まで寄せる */
function scrollActiveTrendWeekIntoView() {
  nextTick(() => {
    const active = trendWeeksScrollRef.value?.querySelector('.is-active')
    active?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
  })
}

/**
 * 最終興行収入予測（DEV-14974）：复用发行方页的 GET /dp/boxOfficePrediction/latest。
 * 取得失败时为 null（卡片显示取得失败文案）；公开版（publicMode）无此接口，不请求也不显示。
 */
const boxOfficePrediction = ref(null)
const boxOfficePredictionLoading = ref(false)
/** 请求序号：切换作品后丢弃旧作品返回的响应 */
let boxOfficePredictionSeq = 0

function fetchBoxOfficePrediction(movieId) {
  const seq = ++boxOfficePredictionSeq
  boxOfficePrediction.value = null
  if (props.publicMode || movieId == null) {
    boxOfficePredictionLoading.value = false
    return Promise.resolve()
  }
  boxOfficePredictionLoading.value = true
  return getBoxOfficePredictionService({ movieId })
    .then((res) => {
      if (seq !== boxOfficePredictionSeq) return
      boxOfficePrediction.value = res ?? null
    })
    .catch((error) => {
      if (seq !== boxOfficePredictionSeq) return
      console.error('[dashboard] getBoxOfficePredictionService failed', error)
      boxOfficePrediction.value = null
    })
    .finally(() => {
      if (seq === boxOfficePredictionSeq) boxOfficePredictionLoading.value = false
    })
}

// 切换电影时先展示 poster loading；无封面或加载失败时改用片名文字占位
const posterLoaded = ref(false)
const posterUseFallback = ref(false)
const posterImgRef = ref(null)
let lastPosterKey = ''

function getPosterKey(movie) {
  if (!movie) return ''
  return `${movie.movieId ?? ''}::${movie.imgUrl || ''}`
}

function syncPosterState() {
  const movie = selectedMovie.value
  const key = getPosterKey(movie)
  // 同一封面重复 sync（如列表轮询刷新）时不要重置，否则缓存图不触发 load 会一直 loading
  if (key && key === lastPosterKey && (posterLoaded.value || posterUseFallback.value)) {
    return
  }
  lastPosterKey = key

  const url = movie?.imgUrl
  if (!url) {
    posterUseFallback.value = true
    posterLoaded.value = true
    return
  }
  posterUseFallback.value = false
  posterLoaded.value = false
  nextTick(() => {
    const img = posterImgRef.value
    if (img?.complete && img.naturalWidth > 0) {
      posterLoaded.value = true
    }
  })
}

watch(
  () => getPosterKey(selectedMovie.value),
  () => {
    syncPosterState()
  },
  { immediate: true },
)

function onPosterLoad() {
  posterLoaded.value = true
}

// 列表缩略图加载失败时回退到默认 poster，避免出现浏览器默认的裂图图标
function onListPosterError(event) {
  const imgEl = event.target
  if (imgEl.src !== resolvedDefaultPosterImg) {
    imgEl.src = defaultPosterImg
  }
}

const trendChartKey = computed(() => {
  const movie = selectedMovie.value
  if (!movie?.boxTrends?.length) return ''
  return `${movie.movieId}-${movie.boxTrends.map((item) => `${item.label}:${item.height}:${item.forecastHeight ?? 0}`).join('|')}`
})

/** 日別予測（DEV-14975）の凡例。公開週トレンド（有料版）で今日〜明後日を含む週のときだけ値を持つ */
const trendForecast = computed(() => (useWeeklyTrend.value
  ? summarizeWeeklyForecast(selectedMovie.value?.boxTrends)
  : null))

/**
 * 予測日ツールチップのタップ表示（タッチ端末用）。ホバーできない端末では列のタップで開閉する。
 * 日付で持つので、週切り替えなどで該当日が無くなれば自然に何も出なくなる
 */
const tappedTrendTipDate = ref('')

function onTrendItemTap(item) {
  if (!item?.tipInfo) return
  // マウス操作の端末はホバーで出すので、クリックで固定表示にはしない
  if (!window.matchMedia('(hover: none)').matches) return
  tappedTrendTipDate.value = tappedTrendTipDate.value === item.date ? '' : item.date
}

// 予測日の列以外をタップしたら閉じる（列自身のタップは onTrendItemTap 側で開閉）
function onDocumentTapForTrendTip(event) {
  if (!tappedTrendTipDate.value) return
  if (event.target?.closest?.('.chart-box-item.has-tip')) return
  tappedTrendTipDate.value = ''
}

function formatRank(rank) {
  return String(rank).padStart(2, '0')
}

// 封面链接打不开（404/网络错误等）时，保留默认 poster 背景并在其上显示片名
const resolvedDefaultPosterImg = new URL(defaultPosterImg, window.location.href).href
function onPosterError(event) {
  if (posterUseFallback.value) return
  const imgEl = event.target
  if (imgEl.src === resolvedDefaultPosterImg) {
    posterUseFallback.value = true
    posterLoaded.value = true
    return
  }
  posterUseFallback.value = true
  posterLoaded.value = true
  imgEl.src = defaultPosterImg
}

function formatNumber(value) {
  return Number(value ?? 0).toLocaleString()
}

function sortMoviesByRank(list) {
  return [...list].sort((a, b) => Number(a.originalRank) - Number(b.originalRank))
}

function cancelMarketRankFetch() {
  marketRankFetchId += 1
  if (marketRankAbortController) {
    marketRankAbortController.abort()
    marketRankAbortController = null
  }
}

function cancelDailyTrendFetch() {
  dailyTrendSessionId += 1
  if (dailyTrendAbortController) {
    dailyTrendAbortController.abort()
    dailyTrendAbortController = null
  }
}

function clearMarketRankLoopTimer() {
  if (marketRankLoopTimer) {
    window.clearTimeout(marketRankLoopTimer)
    marketRankLoopTimer = null
  }
}

function selectMovie(index) {
  if (index < 0 || index >= movies.value.length) return

  cancelMarketRankFetch()
  clearMarketRankLoopTimer()

  const movieId = movies.value[index]?.movieId
  if (movieId == null) return

  // query を積むだけ。詳細ページを開くのは route の watch 側に一本化する
  // （こうしないと戻る操作と直接操作で状態遷移が二重になる）
  detailOpenedByPush = true
  void router.push({ query: { ...route.query, [DETAIL_QUERY_KEY]: String(movieId) } })
}

/**
 * 公開週トレンドの取得パラメータ。
 * 週は日付レンジとは独立に選ぶため date/startDate/endDate は送らない（送ると「今日の週」の既定が崩れる）。
 * 地域条件だけは一覧と揃える。
 */
function buildWeeklyTrendParams(movieId) {
  const params = { movieId }
  if (trendWeekNo.value != null) {
    params.weekNo = trendWeekNo.value
  }
  if (props.nearby) {
    params.nearby = true
  } else {
    if (props.areaIds?.length) params.areaIds = props.areaIds
    if (props.prefectureIds?.length) params.prefectureIds = props.prefectureIds
  }
  return params
}

function buildQueryParams(movieId) {
  const params = {
    movieId: movieId ?? undefined,
    // 単日は date、期間は startDate/endDate（単日を date のまま送るのはサーバー側の当日キャッシュに乗せるため）
    ...buildDateParams(rangeStart.value, props.selectedDate),
  }
  // 無料版は /open/* が limit を受け付けない（サーバー側で25固定）ため送らない
  if (!props.publicMode && marketRankLimit.value !== DEFAULT_MOVIE_LIMIT) {
    params.limit = marketRankLimit.value
  }
  if (props.areaIds?.length) {
    params.areaIds = props.areaIds
  }
  if (props.prefectureIds?.length) {
    params.prefectureIds = props.prefectureIds
  }
  // 近隣モードは中心点（所属劇場）をサーバー側が解決するので、フラグだけ送る
  if (props.nearby) {
    params.nearby = true
  }
  return params
}

function applyMarketRankResponse(res) {
  const list = pickMarketRankList(res)
  const summary = pickMarketRankSummary(res)

  const previousTrendsByMovieId = new Map(
    movies.value
      .filter((item) => Array.isArray(item.boxTrends) && item.boxTrends.length)
      .map((item) => [item.movieId, item.boxTrends]),
  )

  const mappedList = list.map((item, index) => mapMovieRankItem(item, index, props.selectedDate))
  mappedList.forEach((movie) => {
    const previousTrends = previousTrendsByMovieId.get(movie.movieId)
    if (previousTrends?.length) {
      movie.boxTrends = previousTrends
    }
  })
  movies.value = sortMoviesByRank(mappedList)
  nationBoxInfo.value = list.length ? mapNationBoxInfo(summary) : createEmptyNationBoxInfo()
  // 返ってきた件数が要求した limit と同数なら続きがある可能性あり（API は offset を持たないため厳密な総数は不明）
  hasMoreMovies.value = !props.publicMode && list.length >= marketRankLimit.value
}

function isAbortError(error) {
  return error?.name === 'AbortError' || error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED'
}

function abortInFlightRequests(options = {}) {
  const { resetTrendLoading = true } = options

  cancelMarketRankFetch()
  cancelDailyTrendFetch()

  if (resetTrendLoading) {
    trendLoading.value = false
  }
}

function pauseRequests() {
  marketRankLoopStopped = true
  clearMarketRankLoopTimer()
  abortInFlightRequests()
  loading.value = false
  listRefreshing.value = false
  regionRefreshing.value = false
}

function resumeRequests() {
  if (!marketRankLoopStopped) return
  marketRankLoopStopped = false
  scheduleNextMarketRankFetch()
}

function triggerImmediateFetch() {
  if (props.paused) return
  clearMarketRankLoopTimer()
  void runMarketRankLoop()
}

async function fetchMarketRank({ isLoadMore = false } = {}) {
  const fetchId = ++marketRankFetchId
  loading.value = true

  try {
    marketRankAbortController?.abort()
    marketRankAbortController = new AbortController()
    const res = await marketRankService.value(
      buildQueryParams(selectedMovie.value?.movieId),
      { signal: marketRankAbortController.signal },
    )
    marketRankAbortController = null
    if (fetchId !== marketRankFetchId) return false
    applyMarketRankResponse(res)
    if (fetchId !== marketRankFetchId) return false
    await fetchDailyTrendForSelectedMovie()
    return fetchId === marketRankFetchId
  } catch (error) {
    marketRankAbortController = null
    if (fetchId !== marketRankFetchId) return false
    if (isAbortError(error)) return false
    if (isLoadMore) {
      // 追加読み込みの失敗で既存リストを消さない。limit を元に戻し、次のスクロールで再試行できるようにする
      marketRankLimit.value = Math.max(DEFAULT_MOVIE_LIMIT, marketRankLimit.value - LOAD_MORE_STEP)
    } else {
      movies.value = []
      nationBoxInfo.value = createEmptyNationBoxInfo()
    }
    console.error('[dashboard] getMovieMarketRankService failed', error)
    return false
  } finally {
    if (fetchId === marketRankFetchId) {
      loading.value = false
      initialLoading.value = false
      showSkeletonForNextFetch.value = false
      listRefreshing.value = false
      regionRefreshing.value = false
    }
  }
}

function scheduleNextMarketRankFetch() {
  if (marketRankLoopStopped || marketRankLoopTimer) return
  // 期間モードで終点が過去日のときは自動更新しない：データは締め切り済みで変化せず、
  // 3か月分の集計を10分ごとに投げ直すのは BigQuery のコストだけが増える
  if (isRange.value && props.selectedDate.startOf('day').isBefore(today)) return
  marketRankLoopTimer = window.setTimeout(() => {
    marketRankLoopTimer = null
    void runMarketRankLoop()
  }, MARKET_RANK_LOOP_INTERVAL_MS)
}

async function runMarketRankLoop() {
  if (marketRankLoopStopped) return
  if (props.paused && !initialLoading.value) return
  const completed = await fetchMarketRank()
  if (!completed) return
  if (marketRankLoopStopped) return
  if (props.paused && !initialLoading.value) {
    pauseRequests()
    return
  }
  scheduleNextMarketRankFetch()
}

/** 下端スクロールで limit を +LOAD_MORE_STEP して同じ fetchMarketRank を再実行する（DEV-14789：有料版のみ） */
async function loadMoreMovies() {
  if (props.publicMode || props.paused) return
  if (loadingMore.value || loading.value || !hasMoreMovies.value) return
  loadingMore.value = true
  marketRankLimit.value += LOAD_MORE_STEP
  clearMarketRankLoopTimer()
  try {
    const completed = await fetchMarketRank({ isLoadMore: true })
    if (completed && !marketRankLoopStopped && !props.paused) {
      scheduleNextMarketRankFetch()
    }
  } finally {
    loadingMore.value = false
  }
}

/** リストコンテナが下端付近までスクロールされたら追加読み込みを起動する（無料版は limit 固定なので対象外） */
function onMovielistScroll(event) {
  if (props.publicMode || !hasMoreMovies.value) return
  const el = event.target
  const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight
  if (distanceToBottom < 120) {
    void loadMoreMovies()
  }
}

function resetMarketRankPollingCountdown() {
  if (marketRankLoopStopped || props.paused) return
  clearMarketRankLoopTimer()
  scheduleNextMarketRankFetch()
}

function clearIdleStopTimer() {
  if (idleStopTimer) {
    window.clearTimeout(idleStopTimer)
    idleStopTimer = null
  }
}

function scheduleIdleStop() {
  clearIdleStopTimer()
  idleStopTimer = window.setTimeout(() => {
    idleStopTimer = null
    idleStopped = true
    isIdle.value = true
    pauseRequests()
  }, IDLE_STOP_MS)
}

// 数据已过期时立即刷新一次并重启轮询与空闲倒计时
function resumePollingAndCountdown() {
  marketRankLoopStopped = false
  clearMarketRankLoopTimer()
  void runMarketRankLoop()
  scheduleIdleStop()
}

function onUserActivity() {
  // mousemove/scroll 触发频繁，空闲倒计时最多每秒重置一次
  const now = Date.now()
  if (!idleStopped && now - lastIdleResetAt < 1000) return
  lastIdleResetAt = now

  if (idleStopped) {
    idleStopped = false
    isIdle.value = false
    // 停止期间数据已过期：恢复轮询并立即刷新一次
    if (!props.paused) {
      marketRankLoopStopped = false
      clearMarketRankLoopTimer()
      void runMarketRankLoop()
    }
  }
  scheduleIdleStop()
}

// 切换浏览器标签页：隐藏时静默停止轮询（不显示“離席中”），可见时恢复
function onVisibilityChange() {
  if (document.hidden) {
    clearIdleStopTimer()
    pauseRequests()
  }
  else if (!props.paused && !idleStopped) {
    resumePollingAndCountdown()
  }
}

function addActivityListeners() {
  IDLE_ACTIVITY_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, onUserActivity, { passive: true })
  })
  document.addEventListener('visibilitychange', onVisibilityChange)
}

function removeActivityListeners() {
  IDLE_ACTIVITY_EVENTS.forEach((eventName) => {
    window.removeEventListener(eventName, onUserActivity)
  })
  document.removeEventListener('visibilitychange', onVisibilityChange)
}

// 进入 /index 页面（首次挂载或 keep-alive 返回）：开始监听并按需轮询
function activatePage() {
  if (pageActive) return
  pageActive = true
  addActivityListeners()
  if (props.paused || document.hidden) pauseRequests()
  else resumePollingAndCountdown()
}

// 离开 /index 页面（keep-alive 停用或卸载）：停止轮询但不计入“無操作”
function deactivatePage() {
  if (!pageActive) return
  pageActive = false
  removeActivityListeners()
  clearIdleStopTimer()
  isIdle.value = false
  idleStopped = false
  pauseRequests()
}

function fetchDailyTrendForSelectedMovie(options = {}) {
  const movie = selectedMovie.value
  if (!movie?.movieId) return Promise.resolve()

  const sessionId = ++dailyTrendSessionId
  const movieId = movie.movieId

  dailyTrendAbortController?.abort()
  dailyTrendAbortController = null

  dailyTrendFetchChain = dailyTrendFetchChain
    .catch(() => {})
    .then(() => performDailyTrendFetch(sessionId, movieId, options))

  return dailyTrendFetchChain
}

async function performDailyTrendFetch(sessionId, movieId, options = {}) {
  const { resetPolling = false } = options

  if (sessionId !== dailyTrendSessionId) return
  if (selectedMovie.value?.movieId !== movieId) return

  const target = movies.value.find((item) => item.movieId === movieId)
  if (!target) return

  const controller = new AbortController()
  dailyTrendAbortController = controller
  trendLoading.value = true

  const weekly = useWeeklyTrend.value
  try {
    const res = weekly
      ? await getMovieWeeklyTrendService(buildWeeklyTrendParams(movieId), { signal: controller.signal })
      : await dailyTrendService.value(buildQueryParams(movieId), { signal: controller.signal })

    if (sessionId !== dailyTrendSessionId) return
    if (selectedMovie.value?.movieId !== movieId) return

    const targetAfter = movies.value.find((item) => item.movieId === movieId)
    if (!targetAfter) return

    if (weekly) {
      targetAfter.boxTrends = mapWeeklyTrends(pickDailyTrendList(res))
      const prevWeekNo = trendWeek.value?.movieId === movieId ? trendWeek.value.weekNo : null
      trendWeek.value = {
        movieId,
        weekNo: res?.weekNo ?? null,
        // 公開前の放映がなければ 1（旧レスポンス互換も兼ねる）
        minWeekNo: res?.minWeekNo ?? 1,
        maxWeekNo: res?.maxWeekNo ?? 1,
        weekStart: res?.weekStart ?? '',
        weekEnd: res?.weekEnd ?? '',
      }
      if (prevWeekNo !== trendWeek.value.weekNo) scrollActiveTrendWeekIntoView()
    } else {
      targetAfter.boxTrends = mapDailyTrends(pickDailyTrendList(res))
    }
  } catch (error) {
    if (sessionId !== dailyTrendSessionId) return
    if (isAbortError(error)) return
    console.error(weekly
      ? '[dashboard] getMovieWeeklyTrendService failed'
      : '[dashboard] getMovieDailyTrendService failed', error)
  } finally {
    if (dailyTrendAbortController === controller) {
      dailyTrendAbortController = null
    }

    const isCurrentRequest = sessionId === dailyTrendSessionId
      && selectedMovie.value?.movieId === movieId

    if (isCurrentRequest) {
      trendLoading.value = false
    }
    if (resetPolling && isCurrentRequest) {
      resetMarketRankPollingCountdown()
    }
  }
}

onMounted(() => {
  mobileMq = window.matchMedia(MOBILE_BREAKPOINT_MQ)
  syncMobileLayout()
  mobileMq.addEventListener('change', syncMobileLayout)
  document.addEventListener('click', onDocumentTapForTrendTip)
  // ?movie= 付きでリロード／直接開かれた場合に詳細ページを復元する
  syncDetailFromRoute()
  activatePage()
})

// keep-alive 返回页面时恢复轮询
onActivated(() => {
  activatePage()
})

// keep-alive 离开页面时停止轮询（区别于“無操作”，不显示覆盖层）
onDeactivated(() => {
  deactivatePage()
})

onUnmounted(() => {
  if (mobileMq) {
    mobileMq.removeEventListener('change', syncMobileLayout)
    mobileMq = null
  }
  document.removeEventListener('click', onDocumentTapForTrendTip)
  deactivatePage()
  removeActivityListeners()
  clearIdleStopTimer()
  pauseRequests()
})

watch(
  () => [rangeStart.value?.format('YYYY-MM-DD'), props.selectedDate?.format('YYYY-MM-DD')].join('~'),
  () => {
    // 日付（レンジ）が変われば詳細の対象作品も変わるため、query ごと閉じて一覧に戻す
    closeDetail()
  },
)

/** 数组按值比较（排序后逐一比对），用于判断 areaIds/prefectureIds 是否真的变化 */
function idsEqual(a, b) {
  const arrA = Array.isArray(a) ? a : []
  const arrB = Array.isArray(b) ? b : []
  if (arrA.length !== arrB.length) return false
  const sortedA = [...arrA].sort()
  const sortedB = [...arrB].sort()
  return sortedA.every((v, i) => v === sortedB[i])
}

// 地区切替（親で確定された areaIds/prefectureIds/nearby の変化）：選択をリセットし地区ローディングで再取得
watch(
  () => [props.areaIds, props.prefectureIds, props.nearby],
  ([areaIds, prefectureIds, nearby], [prevAreaIds, prevPrefectureIds, prevNearby]) => {
    if (idsEqual(areaIds, prevAreaIds) && idsEqual(prefectureIds, prevPrefectureIds)
      && nearby === prevNearby) return
    closeDetail()
    marketRankLimit.value = DEFAULT_MOVIE_LIMIT
    hasMoreMovies.value = false
    showSkeletonForNextFetch.value = false
    listRefreshing.value = true
    regionRefreshing.value = true
    triggerImmediateFetch()
  },
)

watch(
  () => props.paused,
  (paused) => {
    if (paused) {
      // 首次加载中打开日历，不中断进行中的首屏请求
      if (!initialLoading.value) pauseRequests()
    }
    else resumeRequests()
  },
)

watch(
  () => props.forceRefreshKey,
  () => {
    marketRankLimit.value = DEFAULT_MOVIE_LIMIT
    hasMoreMovies.value = false
    listRefreshing.value = false
    regionRefreshing.value = false
    showSkeletonForNextFetch.value = true
    triggerImmediateFetch()
  },
)

watch(
  () => props.softRefreshKey,
  () => {
    // 前日/翌日など：骨格は出さず、リスト載入マスクのみ
    marketRankLimit.value = DEFAULT_MOVIE_LIMIT
    hasMoreMovies.value = false
    showSkeletonForNextFetch.value = false
    regionRefreshing.value = false
    listRefreshing.value = true
    triggerImmediateFetch()
  },
)
</script>

<style lang="less" scoped>
/* 悬浮细滚动条：透明轨道 + padding-box 缩进，桌面悬停显色 */
.mc-scrollbar() {
  scrollbar-width: thin;
  scrollbar-color: var(--mc-scrollbar-thumb) transparent;
  scrollbar-gutter: stable;

  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  &::-webkit-scrollbar-corner {
    background: transparent;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    margin: 6px 0;
  }

  &::-webkit-scrollbar-thumb {
    min-height: 40px;
    background-color: var(--mc-scrollbar-thumb);
    background-clip: padding-box;
    border: 3px solid transparent;
    border-radius: 999px;
    transition: background-color 0.2s ease;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: var(--mc-scrollbar-thumb-hover);
  }

  &::-webkit-scrollbar-thumb:active {
    background-color: var(--mc-scrollbar-thumb-active);
  }
}

.movie-content {
  /* --mc-* テーマ変数は親 .dashboard-page で定義済み（ヘッダー・スマホ両方の地区ピッカーにも継承させるため） */
  /* 作品詳細（旧・右カラム）は movie-detail-drawer.vue へ移動したため、単一カラムで良い */

  display: flex;
  flex-direction: column;
  position: relative;
  flex: 1;
  gap: 12px;
  min-height: 0;
  overflow: hidden;
  padding: 0;

  &.is-region-loading {
    pointer-events: none;
  }

  &.is-region-loading .region-loading-overlay {
    pointer-events: auto;
  }
}

.bg {
  background: var(--mc-bg);
  border: 1px solid var(--mc-border);
  border-radius: var(--border-radius, 6px);
  box-shadow: var(--shadow-sm);
}

.dashboard-left {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

.dashboard-table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  /* 右侧 16px 与「充足率」列 .last-col 的 padding-right 对齐 */
  padding: 16px 16px 16px 20px;
  background: color-mix(in srgb, var(--brand-color, #f9da49) 10%, var(--bg-panel, #fff));
  border-bottom: 1px solid var(--mc-border);
  color: var(--mc-text);
  font-size: 16px;
}

.left-box {
  display: flex;
  align-items: center;
}

.table-header {
  font-weight: 600;
}

.dashboard-table {
  width: 100%;
  /* 各数据列固定宽度合计约 614px + moviename 列下限 166px。
     主区变窄时保持该下限并让 .movielist 横向滚动，防止列塌陷/行拉高 */
  min-width: 780px;
  border-collapse: collapse;
  table-layout: fixed;

  thead {
    background: var(--mc-bg-soft);
  }

  th {
    height: 36px;
    border-bottom: 1px solid var(--mc-border);
    color: var(--mc-text-muted);
    font-size: 12px;
    font-weight: 500;
    line-height: 36px;
    /* 与下方数据列保持同一右对齐基准，避免表头与数字错位 */
    text-align: right;
  }

  td {
    height: 72px;
    border-bottom: 1px solid var(--mc-border);
    color: var(--mc-text);
    font-size: 14px;
    line-height: 72px;
    text-align: right;
  }

  tbody tr {
    background: var(--mc-row);
    cursor: pointer;
    transition: background 0.15s ease;

    &:nth-child(odd) {
      background: var(--mc-row-alt);
    }

    &:hover {
      background: var(--mc-row-hover);
    }
  }

  tbody .selected-movie {
    background: var(--mc-row-selected) !important;
  }

  .theader {
    line-height: 1.2;
    /* 防止“上映あたり平均動員”等较长表头文字换行导致表头错位 */
    white-space: nowrap;
  }

  .moviename-th,
  .moviename-td {
    box-sizing: border-box;
    /* 吸收其余数据列收紧后腾出的空间，但设置下限，避免主区变窄时塌陷成 0 宽度 */
    min-width: 166px;
    padding-left: 20px;
    text-align: left;
  }

  .moviename-td {
    line-height: 1.4;
    overflow: hidden;
  }

  .box-title-tab,
  .realtime {
    width: 150px;
  }

  .realtime {
    color: var(--mc-accent-strong);
    font-weight: 700;
  }

  /* 数据列改为固定紧凑宽度，缩小列间距 */
  th:nth-child(3),
  td:nth-child(3) {
    width: 80px;
  }

  th:nth-child(4),
  td:nth-child(4) {
    width: 92px;
  }

  th:nth-child(5),
  td:nth-child(5) {
    width: 80px;
  }

  th:nth-child(6),
  td:nth-child(6) {
    /* “上映あたり平均動員”文字较长，需要更宽的列宽避免单行显示不下 */
    width: 132px;
  }

  th:nth-child(7),
  td:nth-child(7) {
    width: 80px;
  }

  .last-col {
    padding-right: 16px;
  }

  .last-tab {
    padding-right: 12px;
  }
}

/* 表头与数据行同属一个 table，靠 thead th 粘性定位实现悬浮表头，
   避免此前"表头/数据分成两个 table"因滚动条宽度导致的列错位问题 */
.dashboard-table thead th {
  position: sticky;
  top: 0;
  z-index: 5;
  background: var(--mc-bg-soft);
}

.movie-desc {
  display: flex;
  align-items: center;
}

.moviename-num {
  width: 28px;
  font-size: 14px;
}

.moviename-index {
  color: var(--mc-text-muted);
  font-size: 12px;
}

/* 缩略图加载慢时，底部 sheen 动画持续播放；图片绘制完成后自然覆盖遮住动画，无需额外状态判断 */
.moviename-poster {
  position: relative;
  flex-shrink: 0;
  width: 32px;
  height: 44px;
  margin-left: 8px;
  overflow: hidden;
  border-radius: 4px;
  background: var(--mc-bg-soft);
}

.moviename-poster-sheen {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    100deg,
    transparent 30%,
    rgba(255, 255, 255, 0.28) 50%,
    transparent 70%
  );
  background-size: 200% 100%;
  animation: poster-loading-sheen 1.4s ease-in-out infinite;
}

.moviename-poster-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.moviename-desc {
  flex: 1;
  margin-left: 10px;
  overflow: hidden;
}

.moviename-name {
  overflow: hidden;
  font-size: 15px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.moviename-info {
  margin-top: 4px;
  color: var(--mc-text-muted);
  font-size: 11px;
  font-weight: 400;
  /* 列宽收紧时も 1 行に保ち、縦方向へ大量改行して行が異常に高くなるのを防ぐ */
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  span + span {
    margin-left: 8px;
  }
}

.movielist-container {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;

  &.is-loading {
    pointer-events: none;
  }
}

.movielist {
  height: 100%;
  /* 中间宽度（侧边栏展开 + 右侧面板）导致主区变窄时，改为横向滚动，
     避免固定列被挤压、moviename 列塌陷成 0 宽度而使数据行异常拉高 */
  overflow-x: auto;
  overflow-y: auto;
  .mc-scrollbar();
}

.movie-list-state {
  padding: 32px 14px;
  color: var(--mc-text-muted);
  font-size: 13px;
  text-align: center;

  .fa-spinner {
    margin-right: 4px;
  }

  &.is-region {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    padding: 48px 14px;
  }
}

/* 日付切替（前日/翌日）時の再読み込み表示。realtime（--mc-accent-strong）と同じ色で目立たせる。
   位置は親 .movie-list-state の text-align: center に任せる：
   .movie-list-state はブロック要素なので幅は .movielist の可視幅と一致し、そのまま中央に出る */
.movie-list-state__loading {
  color: var(--mc-accent-strong, #a31d15);
  font-weight: 700;
}

/* DEV-14789：下端スクロールでの追加読み込み表示。既存行を隠さず末尾に薄く出すだけ */
.movie-list-load-more {
  padding: 14px;
  color: var(--mc-accent-strong);
  font-size: 12px;
  font-weight: 700;
  text-align: center;

  .fa-spinner {
    margin-right: 4px;
  }
}

.region-loading-inline {
  position: relative;
  width: 56px;
  height: 56px;

  &__ripple {
    position: absolute;
    inset: 0;
    border: 2px solid color-mix(in srgb, var(--mc-accent) 80%, transparent);
    border-radius: 50%;
    opacity: 0;
    animation: region-radar-ripple 2s cubic-bezier(0.15, 0.6, 0.3, 1) infinite;

    &--2 { animation-delay: 0.66s; }
    &--3 { animation-delay: 1.32s; }
  }

  &__dot {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 10px;
    height: 10px;
    margin: -5px 0 0 -5px;
    border-radius: 50%;
    background: var(--mc-accent);
    box-shadow: 0 0 10px color-mix(in srgb, var(--mc-accent) 55%, transparent);
  }

  &__text {
    margin: 0;
    /* 地域切替中の読み込み表示のみ --mc-text-muted のまま
       （次ページ読み込み中の文言は --mc-accent-strong に変更済み、こちらは対象外） */
    color: var(--mc-text-muted);
    font-size: 13px;
    font-weight: 600;
  }
}

@keyframes region-radar-ripple {
  0% {
    transform: scale(0.25);
    opacity: 0.85;
  }
  70% {
    opacity: 0.25;
  }
  100% {
    transform: scale(1);
    opacity: 0;
  }
}

.movie-skeleton-wrap {
  padding: 10px 0 0;
  background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--brand-color, #f9da49) 4%, var(--bg-panel, #fff)) 0%,
    transparent 22%
  );
}

.movie-skeleton-row {
  display: grid;
  grid-template-columns: 28px minmax(220px, 1.8fr) 150px 96px 96px 96px 96px;
  align-items: center;
  gap: 10px;
  height: 72px;
  padding: 0 12px 0 20px;
  border-bottom: 1px solid var(--mc-border);
}

.skeleton-movie-meta {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.skeleton-title-lines {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin-top: 2px;
}

.skeleton-block {
  position: relative;
  overflow: hidden;
  height: 11px;
  border-radius: 6px;
  background: color-mix(in srgb, var(--mc-text-muted) 14%, var(--bg-panel, #fff));

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: -160px;
    width: 140px;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent 0%,
      color-mix(in srgb, var(--brand-color, #f9da49) 34%, #fff) 52%,
      transparent 100%
    );
    animation: skeleton-shimmer 1.1s ease-in-out infinite;
  }
}

.skeleton-rank {
  width: 20px;
}

.skeleton-main {
  width: 88%;
}

.skeleton-sub {
  width: 62%;
}

.skeleton-title-main-line {
  width: min(86%, 240px);
  height: 16px;
  border-radius: 8px;
}

.skeleton-title-sub-line {
  width: min(72%, 200px);
  height: 11px;
}

.skeleton-title-sub-line-short {
  width: min(54%, 150px);
}

.skeleton-cell {
  width: 76%;
  justify-self: end;
}

.skeleton-cell.last {
  margin-right: 6px;
}

.skeleton-title {
  width: 120px;
  margin: 8px auto;
}

.skeleton-big {
  width: 160px;
  height: 34px;
  margin: 14px auto;
}

.skeleton-inline {
  width: 132px;
}

.skeleton-poster {
  width: 100%;
  height: 100%;
  border-radius: 6px;
}

.skeleton-poster-card {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border: 1px solid color-mix(in srgb, var(--brand-color, #f9da49) 22%, var(--mc-border));
  border-radius: 6px;
  background:
    linear-gradient(
      180deg,
      color-mix(in srgb, var(--brand-color, #f9da49) 12%, var(--bg-panel, #fff)) 0%,
      color-mix(in srgb, var(--mc-text-muted) 10%, var(--bg-panel, #fff)) 70%
    );
}

.skeleton-poster-glow {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 30% 20%, rgba(255, 255, 255, 0.48) 0%, transparent 48%),
    linear-gradient(120deg, transparent 24%, rgba(255, 255, 255, 0.58) 42%, transparent 62%);
  transform: translateX(-50%);
  animation: poster-sheen 2s ease-in-out infinite;
}

.skeleton-poster-footer {
  position: absolute;
  right: 10px;
  bottom: 10px;
  left: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.skeleton-poster-line {
  width: 66%;
  height: 9px;
  margin-left: auto;
  background: color-mix(in srgb, var(--bg-panel, #fff) 62%, var(--mc-text-muted) 38%);
}

.skeleton-poster-line-main {
  width: 78%;
}

@keyframes skeleton-shimmer {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(460px);
  }
}

@keyframes poster-sheen {
  0% {
    transform: translateX(-52%);
    opacity: 0.35;
  }
  50% {
    transform: translateX(0);
    opacity: 0.55;
  }
  100% {
    transform: translateX(52%);
    opacity: 0.35;
  }
}

.movie-empty {
  height: 120px !important;
  color: var(--mc-text-muted);
  font-size: 14px;
  line-height: 120px !important;
  text-align: center !important;
}

.today-dashboard-block {
  flex-shrink: 0;
}

.dashboard-detail {
  display: flex;
  flex-direction: column;
  /* 親の detail-drawer-body 側で縦スクロールをまとめて処理するため、
     ここでは高さを固定・裁剪せず内容ぶんだけ自然に広がるようにする */
}

.today-dashboard {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 14px 20px;
  line-height: 1.3;
  text-align: left;
  color: var(--mc-text);
}

.today-dashboard-title {
  flex: none;
  color: var(--mc-text-muted);
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}

/* PC の通栏バナーでは横並びにするため、スマホ（.mp-summary）用の中央寄せ margin を打ち消す */
.today-dashboard:not(.mp-summary) .skeleton-title,
.today-dashboard:not(.mp-summary) .skeleton-big {
  margin: 0;
}

.today-dashboard-divider {
  flex: none;
  align-self: stretch;
  width: 1px;
  background: var(--mc-border);
}

.region-picker--header .region-picker__trigger {
  height: 32px;
  padding: 0 12px;
  border-radius: 16px;
  font-size: 13px;
}

.region-picker {
  position: relative;
  display: inline-flex;
  z-index: 12;
}

.region-picker__trigger {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 160px;
  padding: 4px 10px;
  border: 1px solid var(--mc-border);
  border-radius: 14px;
  background: color-mix(in srgb, var(--mc-accent) 18%, var(--mc-bg));
  color: var(--mc-text);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;

  > span:first-child {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.region-picker__caret {
  flex: none;
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 5px solid currentColor;
  opacity: 0.7;
  transition: transform 0.15s ease;
}

.region-picker.is-open .region-picker__caret {
  transform: rotate(180deg);
}

.region-picker__panel {
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  z-index: 20;
  display: flex;
  flex-direction: column;
  min-width: 240px;
  max-height: min(400px, 60vh);
  overflow: hidden;
  border: 1px solid var(--mc-border);
  border-radius: 10px;
  background: var(--mc-bg);
  box-shadow: var(--shadow-sm, 0 8px 24px rgba(0, 0, 0, 0.12));
  transform: translateX(-50%);
}

.region-picker__body {
  display: flex;
  min-height: 0;
  flex: 1;
  overflow: hidden;
}

.region-picker__col {
  display: flex;
  flex-direction: column;
  min-width: 108px;
  max-height: min(300px, 48vh);
  overflow-y: auto;
  .mc-scrollbar();
}

.region-picker__col--area {
  border-right: 1px solid var(--mc-border);
  background: var(--mc-bg-soft);
}

.region-picker__col--pref {
  min-width: 120px;
}

.region-picker__item {
  display: block;
  width: 100%;
  padding: 9px 12px;
  border: 0;
  background: transparent;
  color: var(--mc-text);
  font-size: 12px;
  font-weight: 500;
  text-align: left;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    background: var(--mc-row-hover);
  }

  &.is-active {
    background: var(--mc-row-hover);
    font-weight: 700;
  }

  &.is-selected {
    background: var(--mc-row-selected);
    font-weight: 700;
  }
}

.region-picker__hint {
  padding: 10px 12px;
  color: var(--mc-text-muted);
  font-size: 12px;
}

.region-picker__footer {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 10px;
  border-top: 1px solid var(--mc-border);
  background: var(--mc-bg);
}

.region-picker__draft {
  min-width: 0;
  overflow: hidden;
  color: var(--mc-text-muted);
  font-size: 11px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.region-picker__confirm {
  flex: none;
  min-width: 64px;
  padding: 6px 14px;
  border: 0;
  border-radius: 8px;
  background: var(--brand-color, #f9da49);
  color: #181818;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 1px 3px color-mix(in srgb, #000 12%, transparent);

  &:hover {
    filter: brightness(0.97);
  }

  &:active {
    transform: translateY(1px);
  }
}

.region-picker--mp {
  flex: none;
  z-index: 13;

  .region-picker__trigger {
    padding: 2px 8px;
    border: 0;
    border-radius: 12px;
    background: color-mix(in srgb, var(--mc-accent) 22%, var(--mc-bg));
  }

  .region-picker__panel {
    left: 0;
    transform: none;
  }
}

.nation-box {
  flex: none;
  color: var(--mc-accent-strong);
  font-size: 28px;
  font-weight: 700;
  line-height: 1.1;

  .unit {
    color: var(--mc-text);
    font-size: 14px;
    font-weight: 500;
  }
}

.today-dashboard-view {
  display: flex;
  gap: 18px;
  color: var(--mc-text-muted);
  font-size: 12.5px;

  p {
    margin: 0;
    white-space: nowrap;
  }
}

/* スマホ用 mp-summary（PC では DOM 自体を出さない） */
.mp-summary {
  &__topbar {
    position: relative;
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: flex-start;
    gap: 6px;
    min-height: 24px;
    color: var(--mc-text-muted);
    font-size: 12px;
  }

  &__region {
    display: inline-flex;
    align-items: center;
    flex: none;
    color: var(--mc-text);
    font-weight: 600;
    white-space: nowrap;
  }

  &__mid {
    position: absolute;
    left: 50%;
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: 6px;
    min-width: 0;
    transform: translateX(-50%);
  }

  &__type {
    flex: none;
    color: var(--mc-text);
    font-weight: 600;
    white-space: nowrap;
  }

  &__date {
    color: var(--mc-text);
    font-weight: 600;
    cursor: default;
    user-select: none;
    white-space: nowrap;
    pointer-events: none;
  }

  &__season {
    margin-top: 6px;
    color: var(--mc-accent-strong);
    font-size: 13px;
    text-align: center;
  }

  &__center {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 4px;
  }

  &__figure {
    flex: 1;
    text-align: center;
  }

  &__amount {
    display: flex;
    align-items: baseline;
    justify-content: center;
    color: var(--mc-accent-strong);
  }

  &__num {
    display: inline-flex;
    align-items: baseline;
    font-size: 40px;
    font-weight: 800;
    line-height: 1;
  }

  &__unit {
    margin-left: 4px;
    color: var(--mc-text);
    font-size: 16px;
    font-weight: 700;
  }

  &__stats {
    display: flex;
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid var(--mc-border);
  }

  &__stat {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }

  &__stat-value {
    color: var(--mc-text);
    font-size: 16px;
    font-weight: 700;
  }

  &__stat-unit {
    margin-left: 1px;
    font-size: 12px;
    font-weight: 400;
  }

  &__stat-label {
    color: var(--mc-text-muted);
    font-size: 12px;
  }
}

.mp-daynav {
  width: 34px;
  padding: 8px 0;
  background: color-mix(in srgb, var(--mc-accent) 26%, var(--mc-bg));
  border: 1px solid color-mix(in srgb, var(--mc-accent) 55%, var(--mc-border));
  border-radius: 8px;
  color: var(--mc-text);
  font-size: 13px;
  font-weight: 700;
  line-height: 1.35;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease, transform 0.1s ease;

  &:hover:not(:disabled) {
    background: color-mix(in srgb, var(--mc-accent) 45%, var(--mc-bg));
  }

  &:active:not(:disabled) {
    background: var(--mc-accent);
    transform: scale(0.96);
  }

  &:disabled {
    background: color-mix(in srgb, var(--mc-text-muted) 16%, var(--mc-bg));
    border-color: transparent;
    color: color-mix(in srgb, var(--mc-text-muted) 75%, var(--mc-bg));
    cursor: not-allowed;
  }
}

.movie-detail {
  display: flex;
  flex-direction: column;
  /* 埋め込みの CinemaContent（劇場別ランキング）と縦に並ぶため、自身は内容分の高さのみ取り、
     残りの縦スペースは flex:1 の CinemaContent 側に譲る */
  flex: none;
  min-height: 0;
  /* master(prod) では右カラム 400px 幅に収まっていたブロック。詳細ページ化で全幅に
     広げると本日興行収入と興行シェアが左右端まで離れて読みにくいため、
     prod の詰まり方に近い幅で中央に置く（劇場別ランキングの表だけが全幅を使う）。
     最終興行収入予測の档位が折り返さないよう 560px → 720px に広げた */
  width: 100%;
  max-width: 720px;
  margin-left: auto;
  margin-right: auto;
  margin-top: 0;
  /* 下の劇場別ランキング（CinemaContent）カードと隙間なくくっつかないよう間隔を確保 */
  margin-bottom: 16px;
  padding: 14px 14px 18px;
  line-height: 1.4;
  color: var(--mc-text);
  overflow-x: hidden;
  .mc-scrollbar();
}

.movie-detail-hero {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.movie-data-bar {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  justify-content: flex-start;
  min-height: 0;
}

.poster-img {
  position: relative;
  flex-shrink: 0;
  width: 120px;
  aspect-ratio: 2 / 3;
  margin-bottom: 10px;
  overflow: hidden;
  border: 1px solid var(--mc-border);
  border-radius: var(--border-radius, 6px);
  box-shadow: var(--shadow-md);
  container-type: size;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

/* 无封面时：保留默认 poster 背景，片名叠在上方 */
.poster-fallback {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 10% 8%;
  pointer-events: none;
}

.poster-fallback__name {
  display: -webkit-box;
  max-height: 100%;
  overflow: hidden;
  color: #fff;
  font-size: clamp(10px, 12cqmin, 18px);
  font-weight: 700;
  line-height: 1.35;
  text-align: center;
  word-break: break-word;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 7;
}

/* 切换电影时：先显示 loading 占位动画，图片加载完成后淡入放大到位 */
.poster-img__photo {
  opacity: 0;
  transform: scale(1.04);
  transition:
    opacity 0.35s ease,
    transform 0.45s cubic-bezier(0.22, 1, 0.36, 1);

  &.is-loaded {
    opacity: 1;
    transform: scale(1);
  }
}

.poster-loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: var(--mc-bg-soft, rgba(127, 127, 127, 0.12));
}

.poster-loading__sheen {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    100deg,
    transparent 30%,
    rgba(255, 255, 255, 0.28) 50%,
    transparent 70%
  );
  background-size: 200% 100%;
  animation: poster-loading-sheen 1.4s ease-in-out infinite;
}

.poster-loading__icon {
  color: var(--text-secondary, #4a4a4a);
  font-size: 22px;
  opacity: 0.45;
  animation: poster-loading-pulse 1.4s ease-in-out infinite;
}

@keyframes poster-loading-sheen {
  from {
    background-position: 160% 0;
  }

  to {
    background-position: -60% 0;
  }
}

@keyframes poster-loading-pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.35;
  }

  50% {
    transform: scale(1.12);
    opacity: 0.6;
  }
}

.movie-detail-title {
  width: 100%;
}

.movie-title {
  font-size: 18px;
  font-weight: 700;
}

.movie-title-star {
  margin-left: 4px;
  color: var(--mc-accent);
  font-size: 14px;
}

.movie-box-data {
  margin-top: 6px;
  color: var(--mc-text-muted);
  font-size: 12px;
}

.movie-box {
  flex-shrink: 0;
  margin: 10px 0 8px;
}

.movie-box-detail {
  display: flex;
  text-align: center;
}

.movie-box-block {
  flex: 1;
}

.box-title {
  color: var(--mc-text-muted);
  font-size: 12px;
  font-weight: 500;
}

.sum-box-desc {
  margin-top: 4px;
  color: var(--mc-accent-strong);
  font-size: 20px;
  font-weight: 700;
}

.sum-box-unit {
  font-size: 12px;
}

.movie-box-total {
  margin-bottom: 14px;
}

.movie-box-total .sum-box-num,
.movie-box-view .sum-box-num {
  margin-top: 4px;
  color: var(--mc-text);
  font-size: 18px;
  font-weight: 700;
}

.box-extr {
  margin-top: 4px;
  color: var(--mc-text-muted);
  font-size: 11px;
}

.mbox-chart {
  flex: 0 0 auto;
  margin-top: 0;
  margin-bottom: 4px;
  background: var(--mc-bg-soft);
  border: 1px solid var(--mc-border);
  border-radius: var(--border-radius, 6px);
}

.chart-title {
  padding: 8px 10px;
  border-bottom: 1px solid var(--mc-border);
  background: var(--mc-bg-soft);
  color: var(--mc-text-muted);
  font-size: 12px;
  font-weight: 500;
  line-height: 1.3;
}

.mbox-prediction {
  margin-bottom: 8px;
}

.prediction-body {
  padding: 10px 10px 12px;

  /* FinalPredictionBody は発行方ページ（client-v2）と共用で rem 基準の大きめの字。
     ダッシュボードでは下の「直近興行トレンド」（10〜12px）と揃えるため、ここだけ縮める */
  :deep(.fp-meta) {
    margin-bottom: 8px;
    color: var(--mc-text-muted);
    font-size: 12px;
  }

  :deep(.fp-tiers) {
    gap: 4px;
  }

  :deep(.fp-tier) {
    min-height: 32px;
    padding: 4px 2px;
    font-size: 11px;

    &.is-active {
      font-size: 12px;
    }
  }

  :deep(.fp-note) {
    margin-top: 8px;
    color: var(--mc-text-muted);
    font-size: 11px;
  }

  :deep(.fp-asof) {
    color: var(--mc-text-muted);
    font-size: 10px;

    &.is-stale {
      color: #e6a23c;
    }
  }

  :deep(.fp-empty) {
    min-height: 72px;
    font-size: 12px;
  }
}

.chart-body {
  padding: 10px 6px 16px;
}

/* ---------- 公開週セレクタ（劇場週次詳細 cinema-weekly-drawer の週ボタンと同じ見た目に揃える） ---------- */
.trend-weeks {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px 0;
}

.trend-weeks__nav {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border: 1px solid var(--mc-border);
  border-radius: 12px;
  background: var(--mc-bg);
  color: var(--mc-text);
  font-size: 10px;
  cursor: pointer;

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
}

.trend-weeks__scroll {
  display: flex;
  gap: 6px;
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.trend-weeks__item {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-width: 60px;
  height: 24px;
  padding: 0 10px;
  border: 1px solid var(--mc-border);
  border-radius: 8px;
  background: var(--mc-bg);
  color: var(--mc-text-muted);
  font-size: 11px;
  line-height: 1;
  cursor: pointer;

  &.is-active {
    border-color: var(--mc-accent);
    background: var(--mc-accent);
    color: #1c1c1c;
    font-weight: 700;
  }
}

.trend-weeks__range {
  margin: 6px 0 0;
  color: var(--mc-text-muted);
  font-size: 11px;
  text-align: center;
}

/* 週切り替え中は前の週の棒を薄く残し、読み込み中であることを示す */
.chart-box.is-loading {
  opacity: 0.45;
  transition: opacity 0.2s ease;
}

.chart-box {
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  gap: 2px;
  min-height: 116px;
}

.chart-box-item {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  min-width: 0;
}

.bar-boxnum {
  display: flex;
  flex-shrink: 0;
  align-items: flex-end;
  justify-content: center;
  width: 100%;
  min-height: 28px;
  color: var(--mc-text-muted);
  font-size: 10px;
  line-height: 1.2;
  text-align: center;
}

.bar-chart-wrap {
  display: flex;
  flex: 1;
  align-items: flex-end;
  justify-content: center;
  width: 100%;
  min-height: 54px;
}

.bar-chart {
  flex-shrink: 0;
  width: 22px;
  max-height: 54px;
  background: linear-gradient(
    180deg,
    var(--mc-accent) 0%,
    var(--mc-accent-strong) 100%
  );
  border-radius: 2px 2px 0 0;
  transform-origin: bottom center;
}

.bar-grow-in {
  animation: bar-grow-in 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.bar-fade-in {
  animation: bar-fade-in 0.4s ease both;
}

@keyframes bar-grow-in {
  from {
    transform: scaleY(0);
    opacity: 0.45;
  }

  to {
    transform: scaleY(1);
    opacity: 1;
  }
}

@keyframes bar-fade-in {
  from {
    opacity: 0;
    transform: translateY(4px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.bar-data {
  flex-shrink: 0;
  width: 100%;
  min-height: 18px;
  padding-top: 6px;
  color: var(--text-tertiary, #757575);
  font-size: 10px;
  line-height: 1.2;
  text-align: center;

  /* 週末は劇場週次詳細の軸ラベルと同じく強調色、今日は太字 */
  &.is-weekend {
    color: var(--mc-accent-strong);
  }

  &.is-today {
    color: var(--mc-text);
    font-weight: 700;
  }
}

.bar-data__dow {
  display: block;
  margin-top: 1px;
  font-size: 9px;
  opacity: 0.8;
}

/* ---------- 日別予測（DEV-14975）：販売済み + 予測の残り の積み上げ ---------- */
.bar-boxnum.is-forecast {
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  color: var(--mc-text);
}

/* 予測なしの日：金額の下に注記を縦に並べる（横並びだと金額が折り返して詰まる） */
.bar-boxnum.is-no-forecast {
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
}

.bar-boxnum__tag {
  display: inline-block;
  margin-bottom: 1px;
  padding: 0 3px;
  border: 1px dashed var(--mc-accent);
  border-radius: 2px;
  color: var(--mc-accent-strong);
  font-size: 8px;
  line-height: 1.3;
}

.bar-boxnum__none {
  display: block;
  color: var(--mc-text-muted);
  font-size: 8px;
}

.bar-stack {
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  justify-content: flex-end;

  /* 販売済みと予測の残りの間に 2px のすき間 */
  gap: 2px;
  width: 22px;
  max-height: 54px;
  transform-origin: bottom center;
}

.bar-forecast {
  flex-shrink: 0;
  box-sizing: border-box;
  border: 1px dashed var(--mc-accent);
  border-bottom: 0;
  border-radius: 2px 2px 0 0;

  /* 実績と区別するため薄い斜線（印刷・色覚多様性でも区別できるよう模様で表現） */
  background: repeating-linear-gradient(
    45deg,
    color-mix(in srgb, var(--mc-accent) 45%, transparent) 0 3px,
    color-mix(in srgb, var(--mc-accent) 15%, transparent) 3px 6px
  );
}

.bar-chart--sold {
  border-radius: 0;
}

.trend-forecast-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  align-items: center;
  padding: 10px 4px 0;
  color: var(--mc-text-muted);
  font-size: 10px;
  line-height: 1.4;
}

.trend-forecast-legend__item {
  display: inline-flex;
  gap: 4px;
  align-items: center;

  i {
    display: inline-block;
    width: 10px;
    height: 8px;
    border-radius: 1px;
  }

  .lg-sold {
    background: var(--mc-accent-strong);
  }

  .lg-forecast {
    border: 1px dashed var(--mc-accent);
    background: repeating-linear-gradient(
      45deg,
      color-mix(in srgb, var(--mc-accent) 45%, transparent) 0 2px,
      color-mix(in srgb, var(--mc-accent) 15%, transparent) 2px 4px
    );
  }
}

.trend-forecast-legend__time {
  margin-left: auto;
}

/* ---------- 予測日のホバー詳細（参考：他社ダッシュボードの暗色ツールチップ） ---------- */
.chart-box-item.has-tip {
  position: relative;
  cursor: default;

  /* 列全体をホバー領域にし、ホバー中は列を薄くハイライト */
  &:hover {
    border-radius: 4px;
    background: color-mix(in srgb, var(--mc-accent) 8%, transparent);
  }

  &:hover .trend-tip {
    visibility: visible;
    opacity: 1;
  }

  /* タッチ端末：タップで開いた列 */
  &.is-tapped {
    border-radius: 4px;
    background: color-mix(in srgb, var(--mc-accent) 8%, transparent);
    -webkit-tap-highlight-color: transparent;

    .trend-tip {
      visibility: visible;
      opacity: 1;
    }
  }
}

.trend-tip {
  position: absolute;
  top: 0;
  right: calc(100% + 6px);
  z-index: 5;
  min-width: 180px;
  padding: 8px 10px;
  border-radius: 6px;
  background: rgb(24 24 27 / 94%);
  box-shadow: 0 4px 14px rgb(0 0 0 / 25%);
  color: #f4f4f5;
  font-size: 11px;
  line-height: 1.5;
  text-align: left;
  white-space: nowrap;
  pointer-events: none;
  visibility: hidden;
  opacity: 0;

  /* 左半分の列は右側に出して、カードの左端で切れないようにする */
  .tip-on-right & {
    right: auto;
    left: calc(100% + 6px);
  }
}

.trend-tip__title {
  margin: 0 0 4px;
  font-size: 12px;
  font-weight: 700;
}

.trend-tip__row {
  display: flex;
  gap: 6px;
  align-items: center;
  margin: 0;

  &.is-total {
    font-weight: 700;
  }
}

.trend-tip__swatch {
  flex-shrink: 0;
  width: 10px;
  height: 8px;
  border-radius: 1px;

  &.lg-sold {
    background: var(--mc-accent-strong);
  }

  &.lg-forecast {
    border: 1px dashed var(--mc-accent);
    background: repeating-linear-gradient(
      45deg,
      color-mix(in srgb, var(--mc-accent) 60%, transparent) 0 2px,
      color-mix(in srgb, var(--mc-accent) 25%, transparent) 2px 4px
    );
  }
}

.trend-tip__label {
  flex: 1;
  color: #d4d4d8;
}

.trend-tip__value {
  margin-left: 12px;
  font-variant-numeric: tabular-nums;
}

.trend-tip__sub {
  margin: 4px 0 0;
  color: #a1a1aa;
  font-size: 10px;
}

/* スマホ幅：列の横に出すと狭い画面で見切れるため、グラフ領域（.chart-box）の中央上に重ねて出す */
@media (max-width: 768px) {
  .chart-box {
    position: relative;
  }

  .chart-box-item.has-tip {
    position: static;
  }

  .trend-tip,
  .tip-on-right .trend-tip {
    top: 0;
    right: auto;
    left: 50%;
    transform: translateX(-50%);
  }
}

/* タッチ端末：:hover がタップ後に残り続けることがあるため、ホバーでは出さずタップ（is-tapped）のみで出す */
@media (hover: none) {
  .chart-box-item.has-tip:hover:not(.is-tapped) {
    background: none;

    .trend-tip {
      visibility: hidden;
      opacity: 0;
    }
  }
}

.chart-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 116px;
}

.chart-loading-spinner {
  display: inline-block;
  width: 28px;
  height: 28px;
  border: 3px solid color-mix(in srgb, var(--brand-color, #f9da49) 24%, var(--mc-border));
  border-top-color: var(--brand-color, #f9da49);
  border-radius: 50%;
  animation: chart-loading-spin 0.75s linear infinite;
}

@keyframes chart-loading-spin {
  to {
    transform: rotate(360deg);
  }
}

/* 桌面端：未悬停时滑块半透明，减少视觉干扰 */
@media (hover: hover) and (pointer: fine) {
  .movielist,
  .movie-detail {
    &::-webkit-scrollbar-thumb {
      background-color: color-mix(in srgb, var(--mc-scrollbar-thumb) 42%, transparent);
    }

    &:hover::-webkit-scrollbar-thumb,
    &:focus-within::-webkit-scrollbar-thumb {
      background-color: var(--mc-scrollbar-thumb);
    }

    &:hover::-webkit-scrollbar-thumb:hover,
    &:focus-within::-webkit-scrollbar-thumb:hover {
      background-color: var(--mc-scrollbar-thumb-hover);
    }
  }
}

@media (max-width: 1200px) {
  .movie-content {
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    .mc-scrollbar();
  }

  .dashboard-left {
    flex: none;
  }

  .today-dashboard {
    padding: 12px 14px 16px;
  }

  .movie-detail {
    padding: 10px 10px 14px;
  }

  .poster-img {
    width: 72px;
    margin-bottom: 6px;
  }

  .movie-title {
    font-size: 15px;
  }

  .movie-box-data {
    margin-top: 3px;
    font-size: 11px;
    line-height: 1.35;
  }

  .movie-box {
    margin: 6px 0 6px;
  }

  .movie-box-total {
    margin-bottom: 8px;
  }

  .sum-box-desc {
    font-size: 17px;
  }

  .movie-box-total .sum-box-num,
  .movie-box-view .sum-box-num {
    font-size: 16px;
  }

  .box-extr {
    font-size: 10px;
  }

  .chart-box {
    min-height: 108px;
  }

  .bar-boxnum {
    min-height: 24px;
    font-size: 9px;
  }

  .bar-chart-wrap {
    min-height: 48px;
  }

  .bar-chart,
  .bar-stack {
    width: 18px;
    max-height: 48px;
  }

  .dashboard-left {
    min-height: 480px;
  }
}

@media (max-width: 992px) {
  .dashboard-left {
    min-height: 320px;
  }
}

@media (max-width: 768px) {
  .movie-content {
    gap: 10px;
    overflow-x: hidden;
    overflow-y: auto;
    scrollbar-gutter: auto;

    &::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }

    &::-webkit-scrollbar-thumb {
      border-width: 1px;
      min-height: 28px;
    }
  }

  .movielist,
  .movie-detail {
    scrollbar-gutter: auto;
  }

  .dashboard-table-header {
    padding: 12px 14px;
    font-size: 15px;
    border-radius: var(--border-radius, 6px) var(--border-radius, 6px) 0 0;
  }

  .dashboard-left {
    min-height: 0;
    overflow: visible;
    border-radius: var(--border-radius, 6px);
  }

  .movielist-container {
    overflow-x: hidden;
    overflow-y: visible;
    user-select: none;
    -webkit-user-select: none;
    -webkit-touch-callout: none;
  }

  .movielist {
    height: auto;
    overflow: visible;
    padding: 10px 8px 14px;
    background: color-mix(in srgb, var(--mc-bg-soft) 55%, var(--mc-bg));
    user-select: none;
    -webkit-user-select: none;
    -webkit-touch-callout: none;
  }

  /* 手机端：表格 → 卡片列表（保留全部指标列） */
  .dashboard-table {
    width: 100%;
    min-width: 0;
    table-layout: auto;
    border-collapse: separate;
    border-spacing: 0 12px;
    user-select: none;
    -webkit-user-select: none;

    thead {
      display: none;
    }

    tbody tr {
      display: grid;
      /* 先頭のスペーサー列は海报（.moviename-poster）の左端に合わせる幅
         （.moviename-num 幅 32px + .movie-desc gap 10px − 列間 gap 6px = 36px） */
      grid-template-columns: 36px 1fr 1fr 1fr;
      grid-template-areas:
        'name name name name'
        '. box share count'
        '. showshare avgseat seatrate';
      gap: 8px 6px;
      margin: 0 0 12px;
      padding: 12px 12px 10px;
      border: 1px solid var(--mc-border);
      border-radius: 12px;
      background: var(--mc-row);
      box-shadow: 0 1px 2px color-mix(in srgb, #000 6%, transparent);
      height: auto;
      transition: border-color 0.15s ease, background 0.15s ease, transform 0.15s ease;

      &:nth-child(odd),
      &:nth-child(even) {
        background: var(--mc-row);
      }

      &:active {
        transform: scale(0.995);
      }

      &.selected-movie {
        border-color: color-mix(in srgb, var(--brand-color, #f9da49) 55%, var(--mc-border));
        background: var(--mc-row-selected);
        box-shadow: 0 0 0 1px color-mix(in srgb, var(--brand-color, #f9da49) 35%, transparent);
      }
    }

    td {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-start;
      gap: 2px;
      width: auto !important;
      height: auto;
      padding: 0;
      border: none;
      line-height: 1.35;
      text-align: left;
      font-size: 13px;
      font-weight: 700;
    }

    .moviename-td {
      grid-area: name;
      padding: 0 0 8px;
      margin: 0;
      border-bottom: 1px solid color-mix(in srgb, var(--mc-border) 80%, transparent);
    }

    td:nth-child(2) { grid-area: box; }
    td:nth-child(3) { grid-area: share; }
    td:nth-child(4) { grid-area: count; }
    td:nth-child(5) { grid-area: showshare; }
    td:nth-child(6) { grid-area: avgseat; }
    td:nth-child(7) { grid-area: seatrate; }

    td:nth-child(n + 2)::before {
      content: attr(data-label);
      color: var(--mc-text-muted);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.02em;
      line-height: 1.2;
    }

    .realtime {
      color: var(--mc-accent-strong);
      font-size: 14px;
      font-weight: 700;
    }

    .last-tab {
      padding-right: 0;
    }

    .movie-empty {
      display: block;
      grid-column: 1 / -1;
      padding: 28px 12px !important;
      line-height: 1.4 !important;
      text-align: center !important;
    }
  }

  .movie-desc {
    width: 100%;
    /* 海报放大后与标题顶部对齐，而非垂直居中 */
    align-items: flex-start;
    gap: 10px;
  }

  .moviename-desc {
    flex: 1;
    min-width: 0;
    margin-left: 0;
    padding-top: 2px;
  }

  /* 海报缩略图放大为视觉焦点：32×44 → 72×100，并加圆角+阴影 */
  .moviename-poster {
    width: 72px;
    height: 100px;
    margin-left: 0;
    border-radius: 8px;
    box-shadow: 0 2px 6px color-mix(in srgb, #000 12%, transparent);
  }

  .moviename-num {
    width: 32px;
    padding-top: 2px;
  }

  .moviename-index {
    font-size: 13px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  .moviename-name {
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    font-size: 17px;
    font-weight: 700;
    line-height: 1.3;
  }

  .moviename-info {
    margin-top: 6px;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.4;
  }

  .movie-skeleton-wrap {
    overflow: hidden;
    padding: 8px;
  }

  .movie-skeleton-row {
    display: grid;
    grid-template-columns: 28px 1fr;
    width: 100%;
    min-width: 0;
    height: auto;
    gap: 8px;
    margin-bottom: 12px;
    padding: 12px;
    border: 1px solid var(--mc-border);
    border-radius: 12px;
    background: var(--mc-row);

    .skeleton-cell {
      display: none;
    }
  }

  /* 全国興行サマリー（mp-summary）：PC の通栏バナー用 flex 行レイアウトを打ち消し、従来の縦積みに戻す */
  .today-dashboard.mp-summary {
    display: block;
    margin: 0;
    padding: 12px 14px 16px;
    border-radius: 12px;
    text-align: left;
    background: var(--mc-bg);
    border: 1px solid var(--mc-border);
    box-shadow: var(--shadow-sm, 0 1px 3px rgba(0, 0, 0, 0.06));
  }

  .mp-summary {
    &__num {
      font-size: 46px;
    }

    &__unit {
      font-size: 18px;
    }
  }

  @media (max-width: 380px) {
    .today-dashboard.mp-summary {
      padding: 10px 12px 14px;
    }

    .mp-summary {
      &__topbar {
        gap: 4px;
        font-size: 11px;
      }

      &__num {
        font-size: 38px;
      }

      &__unit {
        font-size: 15px;
      }

      &__stat-value {
        font-size: 15px;
      }
    }

    .mp-daynav {
      width: 30px;
      font-size: 12px;
    }
  }

  /* 作品詳細パネル */
  .movie-detail {
    margin-top: 0;
    /* 下の劇場別ランキング（CinemaContent）カードと隙間なくくっつかないよう間隔を確保 */
    margin-bottom: 12px;
    padding: 0;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid var(--mc-border);
    box-shadow: 0 1px 2px color-mix(in srgb, #000 5%, transparent);
  }

  .movie-detail-hero {
    flex-direction: row;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 14px 12px;
    text-align: left;
    background: color-mix(in srgb, var(--mc-bg-soft) 65%, var(--mc-bg));
    border-bottom: 1px solid color-mix(in srgb, var(--mc-border) 85%, transparent);
  }

  .poster-img {
    width: 88px;
    margin-bottom: 0;
    flex-shrink: 0;
    border-radius: 10px;
    box-shadow: 0 4px 12px color-mix(in srgb, #000 18%, transparent);
  }

  .movie-detail-title {
    flex: 1;
    min-width: 0;
    padding-top: 2px;
  }

  .movie-title {
    font-size: 16px;
    line-height: 1.35;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .movie-title-star {
    font-size: 13px;
    vertical-align: middle;
  }

  .movie-box-data {
    margin-top: 4px;
    font-size: 11px;
    line-height: 1.4;
  }

  .movie-data-bar {
    padding: 12px 12px 14px;
    gap: 10px;
  }

  .movie-box {
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .movie-box-detail {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    text-align: left;
  }

  .movie-box-total {
    margin-bottom: 0;
  }

  .movie-box-block {
    padding: 10px 10px 9px;
    border-radius: 10px;
    background: var(--mc-bg-soft);
    border: 1px solid color-mix(in srgb, var(--mc-border) 85%, transparent);
  }

  .box-title {
    font-size: 10px;
    letter-spacing: 0.02em;
  }

  .sum-box-desc {
    margin-top: 6px;
    font-size: 17px;
    line-height: 1.2;
  }

  .sum-box-unit {
    font-size: 11px;
    font-weight: 600;
  }

  .movie-box-view .sum-box-num {
    margin-top: 6px;
    font-size: 16px;
    line-height: 1.2;
  }

  .box-extr {
    margin-top: 4px;
    font-size: 10px;
    line-height: 1.35;
  }

  .mbox-chart {
    margin-top: 2px;
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid color-mix(in srgb, var(--mc-border) 90%, transparent);
  }

  .chart-title {
    padding: 10px 12px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.02em;
    background: color-mix(in srgb, var(--mc-bg-soft) 80%, var(--mc-bg));
  }

  .chart-body {
    padding: 12px 8px 14px;
    background: var(--mc-bg);
  }

  .chart-box {
    min-height: 108px;
    gap: 4px;
  }

  .chart-box-item {
    padding: 0 2px;
  }

  .bar-boxnum {
    min-height: 22px;
    font-size: 9px;
    font-weight: 600;
    color: var(--mc-accent-strong);
  }

  .bar-chart-wrap {
    min-height: 48px;
  }

  .bar-chart {
    width: 14px;
    max-height: 48px;
    border-radius: 4px 4px 0 0;
  }

  .bar-stack {
    width: 14px;
    max-height: 48px;
  }

  .bar-chart--sold {
    border-radius: 0;
  }

  .bar-data {
    min-height: 16px;
    padding-top: 4px;
    font-size: 9px;
    line-height: 1.25;
    white-space: normal;
    word-break: keep-all;
  }

  .chart-loading {
    min-height: 108px;
  }

  /* 骨架屏：詳細パネルも同じ横並びヒーロー */
  .dashboard-detail .skeleton-poster-card {
    width: 88px;
    min-height: 132px;
  }

  .dashboard-detail .movie-detail.bg .movie-detail-hero {
    flex-direction: row;
    align-items: flex-start;
  }

  .dashboard-detail .poster-img {
    width: 88px;
  }

  .dashboard-detail .skeleton-title-lines {
    flex: 1;
    min-width: 0;
  }
}

html.dark .movie-content {
  .mp-daynav:active:not(:disabled) {
    color: #181818;
  }

  @media (max-width: 768px) {
    .dashboard-table tbody tr {
      box-shadow: 0 1px 3px color-mix(in srgb, #000 35%, transparent);
    }

    .today-dashboard.mp-summary {
      background: var(--mc-bg);
      box-shadow: 0 1px 3px color-mix(in srgb, #000 30%, transparent);
    }

    .movie-detail {
      box-shadow: 0 1px 3px color-mix(in srgb, #000 30%, transparent);
    }

    .poster-img {
      box-shadow: 0 4px 14px color-mix(in srgb, #000 45%, transparent);
    }
  }
}

/* 地区切替ローディング */
.region-loading-fade-enter-active,
.region-loading-fade-leave-active {
  transition: opacity 0.28s ease;
}

.region-loading-fade-enter-from,
.region-loading-fade-leave-to {
  opacity: 0;
}

.region-loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background:
    radial-gradient(
      ellipse 70% 55% at 50% 42%,
      color-mix(in srgb, var(--brand-color, #f9da49) 22%, transparent),
      transparent 70%
    ),
    color-mix(in srgb, var(--bg-body, #f5f5f5) 55%, transparent);
  backdrop-filter: blur(7px);
  -webkit-backdrop-filter: blur(7px);
  pointer-events: auto;
}

.region-loading-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: min(100%, 320px);
  padding: 28px 28px 24px;
  border: 1px solid color-mix(in srgb, var(--brand-color, #f9da49) 35%, var(--mc-border));
  border-radius: 18px;
  background: color-mix(in srgb, var(--mc-bg) 92%, var(--brand-color, #f9da49));
  box-shadow:
    0 16px 40px color-mix(in srgb, #000 16%, transparent),
    0 0 0 1px color-mix(in srgb, #fff 40%, transparent) inset;
  text-align: center;
  animation: region-card-in 0.42s cubic-bezier(0.22, 1, 0.36, 1);
}

.region-loading-card__badge {
  margin-top: 6px;
  padding: 4px 12px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--brand-color, #f9da49) 55%, var(--mc-bg));
  color: #181818;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.region-loading-card__title {
  /* 読み込み表示の文字色は画面全体で --mc-text-muted に統一する */
  color: var(--mc-text-muted);
  font-size: 16px;
  font-weight: 700;
}

.region-loading-card__hint {
  color: var(--mc-text-muted);
  font-size: 12px;
  line-height: 1.4;
}

.region-loading-radar {
  position: relative;
  width: 108px;
  height: 108px;
  margin-bottom: 8px;
  border-radius: 50%;
  background:
    radial-gradient(
      circle at center,
      color-mix(in srgb, var(--brand-color, #f9da49) 28%, transparent) 0%,
      transparent 62%
    );
  overflow: hidden;
}

.region-loading-radar__grid {
  position: absolute;
  inset: 8px;
  border-radius: 50%;
  border: 1px solid color-mix(in srgb, var(--brand-color, #f9da49) 35%, transparent);
  box-shadow:
    inset 0 0 0 18px transparent,
    inset 0 0 0 19px color-mix(in srgb, var(--brand-color, #f9da49) 18%, transparent),
    inset 0 0 0 36px transparent,
    inset 0 0 0 37px color-mix(in srgb, var(--brand-color, #f9da49) 14%, transparent);
  opacity: 0.9;

  &::before,
  &::after {
    content: '';
    position: absolute;
    background: color-mix(in srgb, var(--brand-color, #f9da49) 28%, transparent);
  }

  &::before {
    top: 50%;
    left: 8%;
    right: 8%;
    height: 1px;
    transform: translateY(-50%);
  }

  &::after {
    left: 50%;
    top: 8%;
    bottom: 8%;
    width: 1px;
    transform: translateX(-50%);
  }
}

.region-loading-radar__ripple {
  position: absolute;
  inset: 18px;
  border: 2px solid color-mix(in srgb, var(--accent-orange, #f97316) 75%, var(--brand-color, #f9da49));
  border-radius: 50%;
  opacity: 0;
  animation: region-radar-ripple 2.2s cubic-bezier(0.12, 0.55, 0.28, 1) infinite;

  &--2 { animation-delay: 0.73s; }
  &--3 { animation-delay: 1.46s; }
}

.region-loading-radar__sweep {
  position: absolute;
  inset: 8px;
  border-radius: 50%;
  background: conic-gradient(
    from 0deg,
    transparent 0deg,
    transparent 250deg,
    color-mix(in srgb, var(--brand-color, #f9da49) 8%, transparent) 300deg,
    color-mix(in srgb, var(--accent-orange, #f97316) 55%, transparent) 360deg
  );
  animation: region-radar-sweep 2.2s linear infinite;
  mask-image: radial-gradient(circle, #000 62%, transparent 63%);
  -webkit-mask-image: radial-gradient(circle, #000 62%, transparent 63%);
}

.region-loading-radar__core {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  margin: -17px 0 0 -17px;
  border-radius: 50%;
  background: linear-gradient(
    145deg,
    var(--brand-color, #f9da49),
    color-mix(in srgb, var(--accent-orange, #f97316) 55%, var(--brand-color, #f9da49))
  );
  color: #181818;
  font-size: 14px;
  box-shadow:
    0 0 0 3px color-mix(in srgb, var(--mc-bg) 80%, transparent),
    0 4px 14px color-mix(in srgb, var(--accent-orange, #f97316) 40%, transparent);
  animation: region-core-pulse 1.8s ease-in-out infinite;
}

@keyframes region-card-in {
  from {
    opacity: 0;
    transform: translateY(12px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes region-radar-sweep {
  to {
    transform: rotate(360deg);
  }
}

@keyframes region-core-pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(0.92);
  }
}

html.dark .region-loading-overlay {
  background:
    radial-gradient(
      ellipse 70% 55% at 50% 42%,
      color-mix(in srgb, var(--brand-color, #f9da49) 16%, transparent),
      transparent 70%
    ),
    color-mix(in srgb, #0b0d10 62%, transparent);
}

html.dark .region-loading-card {
  background: color-mix(in srgb, var(--mc-bg) 88%, var(--brand-color, #f9da49));
  box-shadow:
    0 16px 40px rgba(0, 0, 0, 0.45),
    0 0 0 1px color-mix(in srgb, #fff 8%, transparent) inset;
}

html.dark .region-loading-card__badge {
  color: #181818;
}

html.dark .region-loading-radar__core {
  color: #181818;
}

/* 空闲“離席中”覆盖层 */
.idle-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}

.idle-overlay__card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  max-width: 360px;
  padding: 32px 40px;
  border: 1px solid var(--border-color, #e5e5e5);
  border-radius: 16px;
  background: var(--bg-panel, #fff);
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.28);
  text-align: center;
  animation: idle-card-in 0.45s cubic-bezier(0.22, 1, 0.36, 1);
}

.idle-overlay__icon {
  position: relative;
  width: 72px;
  height: 72px;
  margin-bottom: 4px;
}

.idle-overlay__moon {
  position: absolute;
  bottom: 4px;
  left: 6px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  box-shadow: inset -11px -7px 0 0 var(--brand-color, #f9da49);
  transform: rotate(-18deg);
  animation: idle-moon-sway 3.2s ease-in-out infinite;
}

.idle-overlay__zzz {
  position: absolute;
  color: var(--text-secondary, #4a4a4a);
  font-weight: 700;
  line-height: 1;
  opacity: 0;
  animation: idle-zzz-float 2.4s ease-out infinite;
}

.idle-overlay__zzz--1 {
  right: 26px;
  bottom: 30px;
  font-size: 13px;
  animation-delay: 0s;
}

.idle-overlay__zzz--2 {
  right: 16px;
  bottom: 40px;
  font-size: 17px;
  animation-delay: 0.5s;
}

.idle-overlay__zzz--3 {
  right: 4px;
  bottom: 50px;
  font-size: 21px;
  animation-delay: 1s;
}

.idle-overlay__title {
  color: var(--text-primary, #000);
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.idle-overlay__text {
  color: var(--text-secondary, #4a4a4a);
  font-size: 13px;
  line-height: 1.7;
}

.idle-overlay__hint {
  margin-top: 6px;
  color: var(--text-secondary, #4a4a4a);
  font-size: 12px;
  animation: idle-hint-pulse 2s ease-in-out infinite;
}

@keyframes idle-card-in {
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.96);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes idle-moon-sway {
  0%,
  100% {
    transform: rotate(-18deg) translateY(0);
  }

  50% {
    transform: rotate(-10deg) translateY(-4px);
  }
}

@keyframes idle-zzz-float {
  0% {
    opacity: 0;
    transform: translate(0, 6px) scale(0.8);
  }

  25% {
    opacity: 1;
  }

  100% {
    opacity: 0;
    transform: translate(8px, -16px) scale(1.15);
  }
}

@keyframes idle-hint-pulse {
  0%,
  100% {
    opacity: 0.55;
  }

  50% {
    opacity: 1;
  }
}

.idle-fade-enter-active {
  transition: opacity 0.35s ease;
}

.idle-fade-leave-active {
  transition: opacity 0.25s ease;
}

.idle-fade-enter-from,
.idle-fade-leave-to {
  opacity: 0;
}
</style>
